import React from 'react';
import ReactDOM from 'react-dom';
import http from '../../modules/api';
import SaveDialogV2 from '../../modules/dialogs/SaveDialogV2';
import WarningDialog from '../../modules/dialogs/WarningDialog';
import TitleContentDialogV2 from '../../modules/dialogs/TitleContentDialogV2';
import InputDialogV3 from '../../modules/dialogs/InputDialogV3';
import AiImageStudioWorkflowClass from './AiImageStudioWorkflowClass';

const PROMPT_MAX_LENGTH = 1500;
const POLL_INTERVAL_MS = 5000;
const MAX_POLL_COUNT = 120;
const MAX_REFERENCE_IMAGE_COUNT = 3;
const MAX_REFERENCE_DESCRIPTION_LENGTH = 120;
const TUTORIAL_DEMO_VIDEO_URL = '/assets/tutorial/ai-video-studio-sample.mp4';

const VIDEO_STYLE_OPTIONS = [
    { value: '', label: '미선택', desc: '입력한 내용에 맞춰 자동 연출' },
    { value: 'PRODUCT_AD', label: '제품 광고', desc: '제품이 또렷하게 보이는 광고 연출' },
    { value: 'BEAUTY_AD', label: '뷰티 광고', desc: '촉촉하고 세련된 뷰티 광고 연출' },
    { value: 'CINEMATIC', label: '시네마틱', desc: '영화처럼 깊이감 있는 연출' },
    { value: 'SNS_SHORT', label: 'SNS 숏폼', desc: '첫 장면부터 시선을 끄는 숏폼 연출' },
    { value: 'REALISTIC', label: '리얼리틱', desc: '자연스럽고 현실적인 영상 표현' },
];

const CAMERA_ANGLE_OPTIONS = [
    { value: '', label: '미선택', desc: '입력한 내용에 맞춰 촬영 구도를 자동 설정' },
    { value: 'FRONT', label: '정면', desc: '피사체를 정면에서 안정적으로 보여주는 구도' },
    { value: 'CLOSE_UP', label: '클로즈업', desc: '피사체의 디테일을 화면 가득 강조하는 구도' },
    { value: 'LOW', label: '로우 앵글', desc: '아래에서 올려다봐 피사체를 크고 강하게 표현' },
    { value: 'HIGH', label: '하이 앵글', desc: '위에서 내려다봐 장면을 넓고 부드럽게 표현' },
    { value: 'TOP', label: '탑뷰', desc: '피사체를 바로 위에서 내려다보는 구도' },
];

const CAMERA_MOVEMENT_OPTIONS = [
    { value: '', label: '미선택', desc: '입력한 내용에 맞춰 카메라 움직임을 자동 설정' },
    { value: 'STATIC', label: '고정', desc: '카메라 이동 없이 안정적으로 장면을 유지' },
    { value: 'DOLLY_IN', label: '천천히 줌인', desc: '피사체에 서서히 가까워지며 집중도를 높임' },
    { value: 'DOLLY_OUT', label: '천천히 줌아웃', desc: '피사체에서 멀어지며 전체 장면을 보여줌' },
    { value: 'PAN', label: '좌우 패닝', desc: '카메라가 좌우로 움직이며 공간을 따라 보여줌' },
    { value: 'ORBIT', label: '부드러운 오빗', desc: '피사체 주변을 부드럽게 돌며 입체감을 강조' },
];

const ASPECT_RATIO_OPTIONS = [
    { value: '9:16', label: '9:16', desc: '릴스 · 세로형' },
    { value: '16:9', label: '16:9', desc: '가로형' },
];

const DURATION_OPTIONS = [
    { value: 4, label: '4초' },
    { value: 6, label: '6초' },
    { value: 8, label: '8초' },
];
const RESOLUTION_OPTIONS = ['720p', '1080p', '4k'];

const style_css = `
.ai-video-studio-workflow {
    --primary-mint: #00e2e6;
    --primary-mint-hover: #00bfc2;
    --deep-navy: #0A1A2F;
    --deep-navy-hover: #1f2937;
    --bg-color: #f9fafb;
    --border-color: #e5e7eb;
    --text-main: #0A1A2F;
    --text-sub: #6b7280;
    --text-light: #9ca3af;
    --white: #ffffff;
    --radius-lg: 16px;
    --radius-md: 12px;
    --radius-sm: 8px;
    font-family: 'Noto Sans KR', 'Lato', sans-serif;
    color: var(--text-main);
    line-height: 1.6;
    font-size: 15px;
}
.ai-video-studio-workflow * { box-sizing: border-box; }
.ai-video-studio-workflow .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 9999; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(4px); }
.ai-video-studio-workflow .modal-box { background: #fff; width: 98vw; max-width: 1700px; height: 95vh; border-radius: var(--radius-lg); box-shadow: 0 25px 50px -12px rgba(0,0,0,.4); display: flex; flex-direction: column; overflow: hidden; }
.ai-video-studio-workflow .full-page-mode { width: 100%; max-width: none; height: 100vh; height: 100dvh; border-radius: 0; box-shadow: none; }
.ai-video-studio-workflow .studio-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 24px; background: #0f172a; color: #fff; }
.ai-video-studio-workflow .studio-logo { display: flex; align-items: center; gap: 8px; color: #fff; font-size: 20px; font-weight: 800; }
.ai-video-studio-workflow .studio-close { cursor: pointer; font-size: 28px; color: #94a3b8; line-height: 1; }
.ai-video-studio-workflow .studio-close:hover { color: #fff; }
.ai-video-studio-workflow .studio-body { flex: 1; display: flex; min-height: 0; background: #f8fafc; }
.ai-video-studio-workflow .studio-sidebar { width: 280px; flex-shrink: 0; background: #fff; border-right: 1px solid var(--border-color); display: flex; flex-direction: column; }
.ai-video-studio-workflow .sidebar-header { padding: 20px 24px; border-bottom: 1px solid var(--border-color); }
.ai-video-studio-workflow .new-chat-btn { width: 100%; display: flex; justify-content: space-between; align-items: center; padding: 10px 16px; background: #fff; border: 1px solid #cbd5e1; border-radius: 8px; color: var(--deep-navy); font-size: 15px; font-weight: 700; cursor: pointer; }
.ai-video-studio-workflow .new-chat-btn:hover { border-color: var(--primary-mint); background: #f0fdfe; }
.ai-video-studio-workflow .history-filter-wrap { display: flex; gap: 8px; padding: 12px 16px; }
.ai-video-studio-workflow .history-filter-btn { flex: 1; height: 36px; border-radius: 8px; border: 1px solid var(--border-color); background: #fff; color: var(--text-sub); font-size: 13px; font-weight: 700; cursor: pointer; }
.ai-video-studio-workflow .history-filter-btn.active { border-color: var(--primary-mint); color: #0369a1; background: #ecfeff; }
.ai-video-studio-workflow .history-list { flex: 1; min-height: 0; overflow-y: auto; padding: 12px; display: flex; flex-direction: column; gap: 4px; }
.ai-video-studio-workflow .usage-summary-box {
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: #f8fafc;
    border: 1px solid var(--border-color);
    padding: 12px 28px;
    min-width: 125px;
    text-align: center;
}
.ai-video-studio-workflow .usage-summary-title {
    font-size: 12px;
    font-weight: 700;
    color: var(--text-sub);
    text-align: center;
}
.ai-video-studio-workflow .usage-summary-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-align: center;
}
.ai-video-studio-workflow .usage-main-text {
    font-size: 18px;
    font-weight: 800;
    color: var(--deep-navy);
}
.ai-video-studio-workflow .usage-separator {
    color: var(--text-light);
}
.ai-video-studio-workflow .usage-sub-text {
    font-size: 13px;
    font-weight: 600;
    color: #0284c7;
}
.ai-video-studio-workflow .usage-loading-text {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-sub);
}
.ai-video-studio-workflow .history-group-title { font-size: 13px; font-weight: 800; color: #94a3b8; padding: 12px 12px 4px; }
.ai-video-studio-workflow .history-item { max-width: 100%; min-height: 38px; padding: 8px 8px 8px 16px; border-radius: 8px; cursor: pointer; display: flex; align-items: center; gap: 8px; color: var(--text-main); font-size: 15px; font-weight: 500; position: relative; }
.ai-video-studio-workflow .history-item:hover { background: #e0f2fe97; }
.ai-video-studio-workflow .history-item.active { background: #e0f2fe; color: #0369a1; font-weight: 700; }
.ai-video-studio-workflow .history-item-text { min-width: 0; flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ai-video-studio-workflow .history-menu-wrap { position: relative; flex: 0 0 auto; }
.ai-video-studio-workflow .history-menu-trigger { width: 25px; height: 25px; display: inline-flex; align-items: center; justify-content: center; padding: 0; border: 0; border-radius: 5px; background: transparent; color: #94a3b8; font-size: 17px; font-weight: 800; cursor: pointer; }
.ai-video-studio-workflow .history-menu-trigger:hover { background: #fff; color: var(--deep-navy); }
.ai-video-studio-workflow .history-menu { position: absolute; top: 28px; right: 0; z-index: 30; min-width: 88px; padding: 5px; border: 1px solid #dbe3ea; border-radius: 7px; background: #fff; box-shadow: 0 8px 22px rgba(15,23,42,.16); }
.ai-video-studio-workflow .history-menu button { width: 100%; display: block; padding: 7px 9px; border: 0; border-radius: 5px; background: transparent; color: #475569; font-size: 12px; font-weight: 600; text-align: left; cursor: pointer; white-space: nowrap; }
.ai-video-studio-workflow .history-menu button:hover { background: #f1f5f9; }
.ai-video-studio-workflow .history-menu button.delete { color: #dc2626; }
.ai-video-studio-workflow .mobile-history-btn { display: none; height: 34px; padding: 0 12px; border: 1px solid rgba(255,255,255,.18); border-radius: 7px; background: rgba(255,255,255,.08); color: #fff; font-size: 12px; font-weight: 700; cursor: pointer; align-items: center; }
.ai-video-studio-workflow .mobile-history-backdrop { display: none; }
.ai-video-studio-workflow .studio-main-wrap { flex: 1; min-width: 0; display: flex; flex-direction: column; overflow: hidden; }
.ai-video-studio-workflow .workspace-scroll { flex: 1; min-height: 0; overflow: hidden; padding: 32px; background: #f1f5f9; }
.ai-video-studio-workflow .workspace-card { width: 100%; max-width: 1320px; height: 100%; min-height: 0; margin: 0 auto; display: grid; grid-template-columns: minmax(0,1fr) 430px; gap: 24px; align-items: stretch; }
.ai-video-studio-workflow .config-pane, .result-pane { height: 100%; min-height: 0; background: #fff; border: 1px solid var(--border-color); border-radius: var(--radius-lg); box-shadow: 0 4px 15px rgba(0,0,0,.03); overflow: hidden; }
.ai-video-studio-workflow .config-pane { display: flex; flex-direction: column; }
.ai-video-studio-workflow .config-body-scroll { flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain; }
.ai-video-studio-workflow .config-header, .result-header { padding: 16px 24px; background: #f8fafc; border-bottom: 1px solid var(--border-color); }
.ai-video-studio-workflow .config-title, .result-title { font-size: 17px; font-weight: 800; color: var(--deep-navy); }
.ai-video-studio-workflow .config-subtitle { margin-top: 4px; color: var(--text-sub); font-size: 13px; }
.ai-video-studio-workflow .prompt-flex-box { display: flex; gap: 12px; margin: 22px 24px 18px; padding: 14px 16px; border: 2px solid var(--border-color); border-radius: var(--radius-md); transition: .2s; }
.ai-video-studio-workflow .prompt-flex-box:focus-within { border-color: var(--primary-mint); box-shadow: 0 0 0 4px rgba(0,226,230,.1); }
.ai-video-studio-workflow .prompt-input-area { flex: 1; min-width: 0; }
.ai-video-studio-workflow .prompt-textarea { width: 100%; min-height: 72px; max-height: 320px; padding: 0; border: 0; outline: none; resize: vertical; overflow-y: auto; background: transparent; color: var(--deep-navy); font-family: inherit; font-size: 16px; line-height: 1.55; }
.ai-video-studio-workflow .prompt-textarea::placeholder { color: #cbd5e1; }
.ai-video-studio-workflow .prompt-counter { text-align: right; color: #94a3b8; font-size: 11px; margin-top: 4px; }
.ai-video-studio-workflow .compact-image-section { margin-top: 10px; padding-top: 12px; border-top: 1px solid #eef2f5; }
.ai-video-studio-workflow .compact-image-guide { display: flex; align-items: center; gap: 7px; margin-bottom: 9px; color: #64748b; font-size: 12px; line-height: 1.45; }
.ai-video-studio-workflow .compact-image-guide i { flex: 0 0 auto; color: var(--primary-mint-hover); font-size: 13px; }
.ai-video-studio-workflow .compact-image-guide.sample-reference-guide { justify-content: center; color: var(--primary-mint-hover); font-weight: 800; text-align: center; }
.ai-video-studio-workflow .compact-image-guide.sample-reference-guide i { color: var(--primary-mint-hover); }
.ai-video-studio-workflow .sample-scene-guide { margin: 20px 24px 0; padding: 12px 14px; border: 1px solid var(--border-color); border-radius: 10px; background: #f8fafc; }
.ai-video-studio-workflow .sample-scene-guide-title { color: var(--deep-navy); font-size: 13px; font-weight: 800; line-height: 1.4; margin-bottom: 4px; }
.ai-video-studio-workflow .sample-scene-guide-text { color: var(--text-sub); font-size: 13px; line-height: 1.55; }
.ai-video-studio-workflow .compact-image-controls { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.ai-video-studio-workflow .image-control-item { position: relative; display: inline-flex; align-items: center; }
.ai-video-studio-workflow .compact-image-btn { position: relative; min-height: 36px; padding: 0 13px; border: 1px solid #cbd5e1; border-radius: 8px; background: #fff; color: var(--deep-navy); display: inline-flex; align-items: center; gap: 7px; font-family: inherit; font-size: 12px; font-weight: 800; cursor: pointer; transition: .2s; white-space: nowrap; }
.ai-video-studio-workflow .compact-image-btn:hover, .ai-video-studio-workflow .compact-image-btn.active { border-color: var(--primary-mint); background: #f0fdfe; }
.ai-video-studio-workflow .compact-image-btn[aria-disabled='true'] { cursor: not-allowed; opacity: .45; border-color: var(--border-color); background: #f3f4f6; }
.ai-video-studio-workflow .compact-image-btn[aria-disabled='true']:hover { border-color: var(--border-color); background: #f3f4f6; }
.ai-video-studio-workflow .compact-image-btn.has-remove { padding-right: 35px; }
.ai-video-studio-workflow .compact-image-thumb { width: 24px; height: 24px; flex: 0 0 auto; border: 1px solid var(--border-color); border-radius: 6px; background: #f8fafc center/cover no-repeat; }
.ai-video-studio-workflow .compact-image-limit { color: #64748b; font-size: 10px; font-weight: 700; }
.ai-video-studio-workflow .compact-image-remove { position: absolute; top: 50%; right: 7px; z-index: 2; width: 20px; height: 20px; padding: 0; transform: translateY(-50%); border: 0; border-radius: 50%; background: rgba(15,23,42,.78); color: #fff; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; }
.ai-video-studio-workflow .compact-image-remove:hover { background: var(--deep-navy); }
.ai-video-studio-workflow .image-control-item > .pill-option-tooltip {
    left: var(--image-tooltip-left, 50%);
    transform: translateX(var(--image-tooltip-translate-x, -50%)) translateY(10px);
}
.ai-video-studio-workflow .image-control-item > .pill-option-tooltip::before {
    left: var(--image-tooltip-arrow-left, 50%);
}
.ai-video-studio-workflow .image-control-item:hover > .pill-option-tooltip,
.ai-video-studio-workflow .image-control-item:focus-within > .pill-option-tooltip {
    opacity: 1;
    visibility: visible;
    transform: translateX(var(--image-tooltip-translate-x, -50%)) translateY(0);
}
.ai-video-studio-workflow .compact-image-warning { margin-top: 8px; color: #9a3412; font-size: 11px; line-height: 1.45; }

.reference-image-popover-portal-root.ai-video-studio-workflow { position: fixed; inset: 0; z-index: 12000; pointer-events: none; }
.reference-image-popover-portal-root.ai-video-studio-workflow .reference-image-popover { position: fixed; width: min(var(--reference-popover-width, 620px), calc(100vw - 24px)); padding: 0; border: 1px solid #dbe3ea; border-radius: 12px; background: #fff; box-shadow: 0 12px 32px rgba(10,26,47,.18); overflow: visible; pointer-events: auto; animation: aiVideoFadeIn .16s ease; }
.reference-image-popover-portal-root.ai-video-studio-workflow .reference-image-popover-scroll { max-height: calc(100dvh - 24px); overflow-y: auto; padding: 24px; border-radius: inherit; }
.reference-image-popover-portal-root.ai-video-studio-workflow .reference-image-popover.is-positioning { visibility: hidden; }
.reference-image-popover-portal-root.ai-video-studio-workflow .reference-image-popover::before,
.reference-image-popover-portal-root.ai-video-studio-workflow .reference-image-popover::after { content: ''; position: absolute; left: var(--reference-popover-arrow-left, 32px); width: 0; height: 0; transform: translateX(-50%); pointer-events: none; }
.reference-image-popover-portal-root.ai-video-studio-workflow .reference-image-popover.placement-bottom::before { top: -11px; border-left: 11px solid transparent; border-right: 11px solid transparent; border-bottom: 11px solid #dbe3ea; }
.reference-image-popover-portal-root.ai-video-studio-workflow .reference-image-popover.placement-bottom::after { top: -9px; border-left: 10px solid transparent; border-right: 10px solid transparent; border-bottom: 10px solid #fff; }
.reference-image-popover-portal-root.ai-video-studio-workflow .reference-image-popover.placement-top::before { bottom: -11px; border-left: 11px solid transparent; border-right: 11px solid transparent; border-top: 11px solid #dbe3ea; }
.reference-image-popover-portal-root.ai-video-studio-workflow .reference-image-popover.placement-top::after { bottom: -9px; border-left: 10px solid transparent; border-right: 10px solid transparent; border-top: 10px solid #fff; }
.ai-video-studio-workflow .reference-popover-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 4px; }
.ai-video-studio-workflow .reference-popover-title { color: var(--deep-navy); font-size: 14px; font-weight: 800; }
.ai-video-studio-workflow .reference-popover-close { width: 26px; height: 26px; padding: 0; border: 0; border-radius: 50%; background: transparent; color: #64748b; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.ai-video-studio-workflow .reference-popover-close:hover { background: #f1f5f9; color: var(--deep-navy); }
.ai-video-studio-workflow .reference-popover-desc { margin-bottom: 12px; color: #94a3b8; font-size: 11px; line-height: 1.45; }
.ai-video-studio-workflow .reference-popover-grid { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 12px; }
.ai-video-studio-workflow .reference-popover-item { min-width: 0; }
.ai-video-studio-workflow .reference-slot { position: relative; width: 100%; aspect-ratio: 1/1; padding: 5px; border-radius: 12px; display: flex; align-items: center; justify-content: center; overflow: hidden; transition: .2s; }
.ai-video-studio-workflow button.reference-slot { font-family: inherit; }
.ai-video-studio-workflow .reference-slot.filled { border: 2px solid var(--primary-mint); background: #f3f4f6 center/cover no-repeat; }
.ai-video-studio-workflow .reference-slot.filled.sample-fixed-reference { border-color: #dbe3ea; }
.ai-video-studio-workflow .reference-slot.slot-add { border: 2px dashed #cbd5e1; background: #f3f4f6; color: #888c91; cursor: pointer; }
.ai-video-studio-workflow .reference-slot.slot-add:hover { border-color: var(--primary-mint); background: #f0fdfe; color: #00736d; }
.ai-video-studio-workflow .reference-slot-add-inner { width: 100%; text-align: center; }
.ai-video-studio-workflow .reference-slot-add-inner svg { display: block; margin: 0 auto; }
.ai-video-studio-workflow .reference-slot-add-label { margin-top: 4px; color: inherit; font-size: 12px; }
.ai-video-studio-workflow .reference-slot-remove { position: absolute; top: 6px; right: 6px; z-index: 2; width: 25px; height: 25px; padding: 0; border: 1px solid rgba(255,255,255,.7); border-radius: 50%; background: rgba(15,23,42,.78); color: #fff; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.ai-video-studio-workflow .reference-slot-name { margin: 7px 0 5px; overflow: hidden; color: var(--deep-navy); font-size: 11px; font-weight: 800; white-space: nowrap; text-overflow: ellipsis; }
.ai-video-studio-workflow .reference-slot-description { width: 100%; min-height: 54px; padding: 7px 8px; border: 1px solid #cbd5e1; border-radius: 7px; outline: none; resize: vertical; color: var(--deep-navy); font-family: inherit; font-size: 11px; line-height: 1.4; }
.ai-video-studio-workflow .reference-slot-description::placeholder { 
    font-size: 12px !important;
    font-weight: 400 !important;
    color: #9CA3AF !important;
    opacity: 1 !important;
}
.ai-video-studio-workflow .reference-slot-description:focus { border-color: var(--primary-mint); box-shadow: 0 0 0 3px rgba(0,226,230,.1); }
.ai-video-studio-workflow .reference-user-fields { display: flex; flex-direction: column; gap: 7px; }
.ai-video-studio-workflow .reference-user-field { display: flex; flex-direction: column; gap: 3px; }
.ai-video-studio-workflow .reference-user-field-label { color: #64748b; font-size: 10px; font-weight: 700; line-height: 1.35; }
.ai-video-studio-workflow .reference-user-input { width: 100%; height: 34px; padding: 0 8px; border: 1px solid #cbd5e1; border-radius: 7px; outline: none; background: #fff; color: var(--deep-navy); font-family: inherit; font-size: 12px; }
.ai-video-studio-workflow .reference-user-input::placeholder { 
    font-size: 12px !important;
    font-weight: 400 !important;
    color: #9CA3AF !important;
    opacity: 1 !important;
}
.ai-video-studio-workflow .reference-user-input:focus { border-color: var(--primary-mint); box-shadow: 0 0 0 3px rgba(0,226,230,.1); }
.ai-video-studio-workflow .reference-popover-note { margin-top: 10px; padding: 8px 10px; border: 1px solid var(--border-color); border-radius: 8px; background: #f8fafc; color: var(--text-sub); font-size: 11px; line-height: 1.45; }

.ai-video-studio-workflow .image-source-modal-overlay { position: fixed; inset: 0; z-index: 12000; display: flex; align-items: center; justify-content: center; padding: 18px; background: rgba(10,26,47,.6); animation: aiVideoFadeIn .2s ease; }
.ai-video-studio-workflow .image-source-modal-box { position: relative; width: 540px; max-width: 100%; padding: 30px; border-radius: 16px; background: var(--white); box-shadow: 0 20px 50px rgba(10,26,47,.2); animation: aiVideoSlideUp .3s ease; }
.ai-video-studio-workflow .image-source-modal-close { position: absolute; top: 16px; right: 16px; z-index: 1; width: 24px; height: 24px; padding: 2px; border: 0; border-radius: 50%; background: transparent; color: #0e1d32; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: .2s; }
.ai-video-studio-workflow .image-source-modal-close:hover { background: #e2e8f0; color: var(--deep-navy); }
.ai-video-studio-workflow .image-source-modal-title { margin: 0 0 20px; color: var(--deep-navy); font-size: 20px; font-weight: 800; }
.ai-video-studio-workflow .image-source-options { display: grid; grid-template-columns: repeat(2,1fr); gap: 15px; margin: 20px 0 0; }
.ai-video-studio-workflow .image-source-option { padding: 22px; border: 1px solid #dbdbdb; border-radius: 8px; background: #fff; color: var(--deep-navy); text-align: center; cursor: pointer; transition: .2s; }
.ai-video-studio-workflow .image-source-option:hover { border-color: var(--primary-mint); background: #f0fdfe; }
.ai-video-studio-workflow .image-source-option-icon { width: 42px; height: 42px; margin: 0 auto 10px; display: flex; align-items: center; justify-content: center; }
.ai-video-studio-workflow .image-source-option-title { margin: 0; color: var(--deep-navy); font-size: 1.1em; font-weight: 600; }
.ai-video-studio-workflow .image-source-option-desc { margin: 6px 0 0; color: #8e8e8e; font-size: 12px; }
@keyframes aiVideoFadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes aiVideoSlideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.ai-video-studio-workflow .settings-wrap { padding: 0 24px 24px; display: flex; flex-direction: column; gap: 20px; }
.ai-video-studio-workflow .setting-section-title { display: block; margin-bottom: 9px; color: #64748b; font-size: 13px; font-weight: 800; }
.ai-video-studio-workflow .setting-section-hint { margin-left: 6px; color: #94a3b8; font-size: 11px; font-weight: 500; }
.ai-video-studio-workflow .pill-option-group { display: flex; flex-wrap: wrap; gap: 8px; }
.ai-video-studio-workflow .pill-option-item { position: relative; display: inline-flex; }
.ai-video-studio-workflow .pill-option-btn { min-height: 36px; padding: 7px 14px; border: 1px solid var(--border-color); border-radius: 30px; background: #fff; color: var(--text-sub); font-family: inherit; font-size: 13px; font-weight: 700; cursor: pointer; transition: .2s; }
.ai-video-studio-workflow .pill-option-btn:hover { border-color: var(--primary-mint); background: #f8fafc; }
.ai-video-studio-workflow .pill-option-btn.active { border-color: var(--primary-mint); color: #0369a1; background: #ecfeff; }
.ai-video-studio-workflow .pill-option-btn:disabled { cursor: not-allowed; opacity: .45; background: #f3f4f6; }
.ai-video-studio-workflow .pill-option-tooltip { position: absolute; top: 100%; left: var(--image-tooltip-left, 50%); z-index: 2000; margin-top: 7px; padding: 6px 12px; border-radius: 6px; background: var(--deep-navy); color: #fff; font-size: 12px; font-weight: 600; line-height: 1.4; white-space: nowrap; pointer-events: none; opacity: 0; visibility: hidden; transform: translateX(var(--image-tooltip-translate-x, -50%)) translateY(10px); transition: opacity .2s cubic-bezier(.4,0,.2,1), visibility .2s cubic-bezier(.4,0,.2,1), transform .2s cubic-bezier(.4,0,.2,1); box-shadow: 0 4px 12px rgba(10,26,47,.15); }
.ai-video-studio-workflow .pill-option-tooltip::before { content: ''; position: absolute; bottom: 100%; left: var(--image-tooltip-arrow-left, 50%); transform: translateX(-50%); border-width: 5px; border-style: solid; border-color: transparent transparent var(--deep-navy) transparent; }
.ai-video-studio-workflow .pill-option-tooltip.placement-top { top: auto; bottom: 100%; margin-top: 0; margin-bottom: 7px; transform: translateX(var(--image-tooltip-translate-x, -50%)) translateY(-10px); }
.ai-video-studio-workflow .pill-option-tooltip.placement-top::before { top: 100%; bottom: auto; border-color: var(--deep-navy) transparent transparent transparent; }
.ai-video-studio-workflow .pill-option-item:hover .pill-option-tooltip,
.ai-video-studio-workflow .pill-option-item:focus-within .pill-option-tooltip { opacity: 1; visibility: visible; transform: translateX(var(--image-tooltip-translate-x, -50%)) translateY(0); }
.ai-video-studio-workflow .pill-option-item:hover .pill-option-tooltip.placement-top,
.ai-video-studio-workflow .pill-option-item:focus-within .pill-option-tooltip.placement-top { transform: translateX(var(--image-tooltip-translate-x, -50%)) translateY(0); }
.ai-video-studio-workflow .generation-note { padding: 12px 14px; border-radius: 10px; background: #f8fafc; border: 1px solid var(--border-color); color: var(--text-sub); font-size: 12px; line-height: 1.55; }
.ai-video-studio-workflow .generate-footer { padding: 12px 24px; border-top: 1px solid var(--border-color); background: #f8fafc; display: flex; justify-content: flex-end; }
.ai-video-studio-workflow .btn { height: 44px; padding: 0 20px; border-radius: 8px; border: 0; display: inline-flex; align-items: center; justify-content: center; gap: 7px; font-family: inherit; font-size: 14px; font-weight: 800; cursor: pointer; transition: .2s; white-space: nowrap; }
.ai-video-studio-workflow .btn-primary { background: var(--primary-mint); color: var(--deep-navy); box-shadow: 0 2px 8px rgba(0,226,230,.2); }
.ai-video-studio-workflow .btn-primary:hover { background: var(--primary-mint-hover); transform: translateY(-1px); }
.ai-video-studio-workflow .btn-secondary { background: var(--deep-navy); color: #fff; }
.ai-video-studio-workflow .btn-secondary:hover { background: var(--deep-navy-hover); }
.ai-video-studio-workflow .btn-outline { background: #fff; color: var(--deep-navy); border: 1px solid #cbd5e1; }
.ai-video-studio-workflow .btn-outline:hover { border-color: var(--primary-mint); background: #f0fdfe; }
.ai-video-studio-workflow .btn-sm { height: 34px; padding: 0 12px; font-size: 12px; }
.ai-video-studio-workflow .btn:disabled { cursor: not-allowed; opacity: .45; transform: none; }
.ai-video-studio-workflow .result-pane { display: flex; flex-direction: column; }
.ai-video-studio-workflow .result-body-scroll { flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain; display: flex; flex-direction: column; }
.ai-video-studio-workflow .result-header { display: flex; justify-content: space-between; align-items: center; }
.ai-video-studio-workflow .result-count { color: var(--text-sub); font-size: 12px; font-weight: 700; }
.ai-video-studio-workflow .video-canvas-wrap { flex: 1; min-height: 0; padding: 20px; display: flex; align-items: center; justify-content: center; }
.ai-video-studio-workflow .video-canvas { position: relative; width: 100%; max-height: 610px; border-radius: 12px; overflow: hidden; border: 1px solid var(--border-color); background: #0f172a; display: flex; align-items: center; justify-content: center; }
.ai-video-studio-workflow .video-canvas.ratio-vertical { aspect-ratio: 9/16; max-width: 330px; }
.ai-video-studio-workflow .video-canvas.ratio-horizontal { aspect-ratio: 16/9; max-width: 100%; }
.ai-video-studio-workflow .video-preview { width: 100%; height: 100%; object-fit: contain; background: #111827; }
.ai-video-studio-workflow .video-preload-wait { position: absolute; inset: 0; z-index: 3; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; background: #0f172a; color: #cbd5e1; font-size: 12px; }
.ai-video-studio-workflow .video-default { padding: 30px; text-align: center; color: #94a3b8; }
.ai-video-studio-workflow .video-default-title { color: #cbd5e1; font-size: 15px; font-weight: 800; margin-bottom: 6px; }
.ai-video-studio-workflow .video-loading { display: flex; flex-direction: column; align-items: center; gap: 14px; color: #cbd5e1; }
.ai-video-studio-workflow .spinner { width: 34px; height: 34px; border: 3px solid rgba(255,255,255,.18); border-top-color: var(--primary-mint); border-radius: 50%; animation: aiVideoSpin 1s linear infinite; }
.ai-video-studio-workflow .video-error { padding: 24px; color: #fecaca; text-align: center; font-size: 13px; }
.ai-video-studio-workflow .preview-status-badge { position: absolute; top: 12px; left: 12px; z-index: 4; padding: 5px 11px; border-radius: 20px; background: rgba(10,26,47,.84); color: #fff; font-size: 12px; font-weight: 800; }
.ai-video-studio-workflow .download-icon-btn { position: absolute; top: 12px; right: 12px; z-index: 5; width: 36px; height: 36px; border: 1px solid rgba(0,0,0,.05); border-radius: 8px; background: rgba(255,255,255,.95); color: #334155; cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,.15); display: flex; align-items: center; justify-content: center; font-size: 18px; }
.ai-video-studio-workflow .tutorial-demo-badge { position: absolute; left: 12px; top: 12px; z-index: 6; padding: 6px 10px; border-radius: 999px; background: rgba(255,255,255,.92); color: #173941; font-size: 12px; font-weight: 700; }
.ai-video-studio-workflow .history-nav-overlay { position: absolute; left: 50%; bottom: 7px; transform: translateX(-50%); z-index: 5; display: flex; align-items: center; gap: 14px; padding: 6px 14px; border-radius: 24px; background: rgba(10,26,47,.78); color: #fff; border: 1px solid rgba(255,255,255,.18); backdrop-filter: blur(4px); }
.ai-video-studio-workflow .history-nav-btn { width: 25px; height: 25px; padding: 0; border: 0; border-radius: 50%; background: transparent; color: #fff; cursor: pointer; font-size: 18px; display: flex; align-items: center; justify-content: center; }
.ai-video-studio-workflow .history-nav-btn:hover { background: rgba(255,255,255,.16); }
.ai-video-studio-workflow .history-nav-btn.disabled { opacity: .3; cursor: default; }
.ai-video-studio-workflow .history-nav-index { min-width: 42px; text-align: center; font-size: 12px; font-weight: 800; }
.ai-video-studio-workflow .result-meta { padding: 0 20px 18px; color: var(--text-sub); font-size: 12px; text-align: center; }
.ai-video-studio-workflow .workspace-footer { min-height: 80px; padding: 16px 28px; background: #fff; border-top: 1px solid var(--border-color); display: flex; align-items: center; justify-content: space-between; gap: 20px; box-shadow: 0 -4px 20px rgba(10,26,47,.05); }
.ai-video-studio-workflow .footer-guide { flex: 1; display: flex; align-items: center; gap: 14px; min-width: 0; }
.ai-video-studio-workflow .footer-guide-icon { width: 46px; height: 46px; flex-shrink: 0; border-radius: 10px; background: rgba(0,226,230,.1); display: flex; align-items: center; justify-content: center; color: var(--primary-mint-hover); font-size: 20px; font-weight: 900; }
.ai-video-studio-workflow .footer-guide-title { color: var(--deep-navy); font-size: 16px; font-weight: 800; }
.ai-video-studio-workflow .footer-guide-desc { color: var(--text-sub); font-size: 13px; }
.ai-video-studio-workflow .footer-actions { display: flex; gap: 10px; flex-shrink: 0; }
@keyframes aiVideoSpin { to { transform: rotate(360deg); } }
@media (max-width: 1100px) {
    .ai-video-studio-workflow .workspace-scroll { overflow-y: auto; }
    .ai-video-studio-workflow .workspace-card { height: auto; min-height: 100%; grid-template-columns: 1fr; }
    .ai-video-studio-workflow .config-pane, .ai-video-studio-workflow .result-pane { height: auto; }
    .ai-video-studio-workflow .config-body-scroll, .ai-video-studio-workflow .result-body-scroll { overflow: visible; }
    .ai-video-studio-workflow .result-pane { min-height: 560px; }
    .ai-video-studio-workflow .video-canvas.ratio-horizontal { max-width: 720px; }
}
@media (max-width: 850px) {
    .ai-video-studio-workflow .modal-box { width: 100vw; height: 100vh; height: 100dvh; border-radius: 0; }
    .ai-video-studio-workflow .studio-sidebar { position: absolute; left: 0; top: 0; bottom: 0; z-index: 130; width: min(82vw,320px); transform: translateX(-100%); transition: transform .25s ease; box-shadow: 0 10px 30px rgba(0,0,0,.18); }
    .ai-video-studio-workflow .studio-sidebar.mobile-open { transform: translateX(0); }
    .ai-video-studio-workflow .mobile-history-btn { display: inline-flex; }
    .ai-video-studio-workflow .mobile-history-backdrop { display: block; position: absolute; inset: 0; z-index: 120; background: rgba(15,23,42,.45); }
    .ai-video-studio-workflow .workspace-scroll { padding: 18px 14px; }
    .ai-video-studio-workflow .workspace-footer { flex-direction: column; align-items: stretch; }
    .ai-video-studio-workflow .footer-actions { width: 100%; }
    // .ai-video-studio-workflow .footer-actions .btn { flex: 1; }
}
@media (max-width: 520px) {
    .ai-video-studio-workflow .compact-image-controls { align-items: stretch; }
    .ai-video-studio-workflow .image-control-item { flex: 1 1 calc(50% - 4px); }
    .ai-video-studio-workflow .compact-image-btn { width: 100%; justify-content: center; }
    .reference-image-popover-portal-root.ai-video-studio-workflow .reference-image-popover { width: min(var(--reference-popover-width, 620px), calc(100vw - 20px)); }
    .reference-image-popover-portal-root.ai-video-studio-workflow .reference-image-popover-scroll { max-height: calc(100dvh - 20px); padding: 20px 16px; }
    .ai-video-studio-workflow .reference-popover-grid { grid-template-columns: repeat(3,minmax(0,1fr)); }
    .ai-video-studio-workflow .image-source-options { grid-template-columns: 1fr; }
    .ai-video-studio-workflow .reference-image-grid { grid-template-columns: 1fr 1fr; }
    .ai-video-studio-workflow .studio-header { padding: 12px 14px; }
    .ai-video-studio-workflow .studio-logo { font-size: 16px; }
    .ai-video-studio-workflow .prompt-flex-box, .ai-video-studio-workflow .source-image-wrap { margin-left: 14px; margin-right: 14px; }
    .ai-video-studio-workflow .sample-scene-guide { margin-left: 14px; margin-right: 14px; }
    .ai-video-studio-workflow .settings-wrap { padding-left: 14px; padding-right: 14px; }
    .ai-video-studio-workflow .generate-footer { padding: 14px; }
    .ai-video-studio-workflow .generate-footer .btn { width: 100%; }
    .ai-video-studio-workflow .footer-actions { flex-direction: column; }
}
`;

function firstNonEmpty(...values) {
    for (let i = 0; i < values.length; i += 1) {
        const value = values[i];
        if (value !== undefined && value !== null && String(value).trim() !== '') {
            return value;
        }
    }
    return '';
}

function normalizeAspectRatio(value) {
    const normalized = String(value || '').trim().replace('/', ':');
    return normalized === '16:9' ? '16:9' : '9:16';
}

function normalizeUserReferenceImageConfig(value) {
    let config = value;
    if (typeof config === 'string') {
        try { config = JSON.parse(config); } catch (e) { config = null; }
    }
    if (!config || typeof config !== 'object' || Array.isArray(config)) return null;

    const rawFields = config.input_fields || config.inputFields;
    const inputFields = Array.isArray(rawFields) ? rawFields.map((field) => ({
        key: String(field && (field.key || field.name) || '').trim(),
        label: String(field && field.label || '').trim(),
        type: String(field && field.type || 'text').toLowerCase() === 'textarea' ? 'textarea' : 'text',
        required: field && field.required === false ? false : true,
        placeholder: String(field && field.placeholder || ''),
        max_length: Math.max(1, Math.min(MAX_REFERENCE_DESCRIPTION_LENGTH, Number(field && (field.max_length || field.maxLength) || MAX_REFERENCE_DESCRIPTION_LENGTH))),
    })).filter((field) => field.key) : [];

    const maxCount = Math.max(1, Math.min(MAX_REFERENCE_IMAGE_COUNT, Number(config.max_count || config.maxCount || 1)));
    const minCount = Math.max(0, Math.min(maxCount, Number(config.min_count || config.minCount || (config.required === false ? 0 : 1))));

    return {
        required: config.required === false ? false : true,
        min_count: minCount,
        max_count: maxCount,
        image_guide: String(config.image_guide || config.imageGuide || ''),
        image_description: String(config.image_description || config.imageDescription || ''),
        add_label: String(config.add_label || config.addLabel || '이미지 추가'),
        slot_label: String(config.slot_label || config.slotLabel || '참조 이미지'),
        image_validation_message: String(config.image_validation_message || config.imageValidationMessage || ''),
        field_validation_message: String(config.field_validation_message || config.fieldValidationMessage || ''),
        input_fields: inputFields,
        description_template: String(config.description_template || config.descriptionTemplate || ''),
    };
}

function escapeRegExp(value) {
    return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function parseReferenceInputValues(config, description) {
    const normalizedConfig = normalizeUserReferenceImageConfig(config);
    const fields = normalizedConfig ? normalizedConfig.input_fields : [];
    const template = normalizedConfig ? normalizedConfig.description_template : '';
    const raw = String(description || '').trim();
    const emptyValues = fields.reduce((result, field) => ({ ...result, [field.key]: '' }), {});
    if (!fields.length || !raw) return emptyValues;

    if (!template) {
        if (fields.length === 1) return { ...emptyValues, [fields[0].key]: raw };
        return emptyValues;
    }

    const keys = [];
    let pattern = '^';
    let lastIndex = 0;
    const placeholderRegex = /\{([a-zA-Z0-9_]+)\}/g;
    let match;
    while ((match = placeholderRegex.exec(template)) !== null) {
        pattern += escapeRegExp(template.slice(lastIndex, match.index));
        pattern += '(.*?)';
        keys.push(match[1]);
        lastIndex = match.index + match[0].length;
    }
    pattern += escapeRegExp(template.slice(lastIndex));
    pattern += '$';

    const parsed = raw.match(new RegExp(pattern));
    if (!parsed) {
        if (fields.length === 1) return { ...emptyValues, [fields[0].key]: raw };

        // 저장 문자열의 공백이 정규화된 경우에도 props의 필드 순서를 기준으로 복원합니다.
        const slashParts = raw.split(/\s*\/\s*/);
        if (slashParts.length === fields.length) {
            const fallbackValues = { ...emptyValues };
            fields.forEach((field, index) => {
                const part = String(slashParts[index] || '').trim();
                const colonIndex = part.indexOf(':');
                fallbackValues[field.key] = String(colonIndex >= 0 ? part.slice(colonIndex + 1) : part).trim();
            });
            return fallbackValues;
        }
        return emptyValues;
    }

    const values = { ...emptyValues };
    keys.forEach((key, index) => {
        if (Object.prototype.hasOwnProperty.call(values, key)) values[key] = String(parsed[index + 1] || '').trim();
    });
    return values;
}

function getReferenceInputValues(config, item = {}) {
    const parsed = parseReferenceInputValues(config, item.description || '');
    const stored = item.inputValues || item.input_values;
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return parsed;
    return { ...parsed, ...stored };
}

function formatReferenceDescription(config, item = {}) {
    const normalizedConfig = normalizeUserReferenceImageConfig(config);
    if (!normalizedConfig) return String(item.description || '').trim();
    const fields = normalizedConfig.input_fields;
    if (!fields.length) return '';
    const values = getReferenceInputValues(normalizedConfig, item);
    const template = normalizedConfig.description_template;
    if (!template) return fields.length === 1 ? String(values[fields[0].key] || '').trim() : '';
    return template
        .replace(/\{([a-zA-Z0-9_]+)\}/g, (_, key) => String(values[key] || '').trim())
        .trim()
        .slice(0, MAX_REFERENCE_DESCRIPTION_LENGTH);
}

function normalizeReferenceImages(value) {
    let list = value;
    if (typeof list === 'string') {
        try { list = JSON.parse(list); } catch (e) { list = []; }
    }
    if (!Array.isArray(list)) return [];
    return list.slice(0, MAX_REFERENCE_IMAGE_COUNT).map((item, index) => ({
        id: item.id || `reference-${Date.now()}-${index}-${Math.random()}`,
        imageUrl: item.image_url || item.imageUrl || '',
        imageBase64: item.image_base64 || item.imageBase64 || '',
        mimeType: item.image_mime_type || item.mimeType || 'image/png',
        fileName: item.file_name || item.fileName || `참조 이미지 ${index + 1}`,
        description: String(item.description || '').slice(0, MAX_REFERENCE_DESCRIPTION_LENGTH),
        inputValues: item.input_values && typeof item.input_values === 'object'
            ? { ...item.input_values }
            : (item.inputValues && typeof item.inputValues === 'object' ? { ...item.inputValues } : {}),
    })).filter((item) => item.imageUrl || item.imageBase64);
}

const STUDIO_TUTORIAL_DEMO_EVENT = 'instagram-tutorial:studio-demo-action';

export default class AiVideoStudioWorkflowClass extends React.Component {
    constructor(props) {
        super(props);

        this.pollTimer = null;
        this.activePollResultSeq = null;
        this.currentSaveTimer = null;
        this.currentSavePromise = null;
        this.fileInputRef = React.createRef();
        this.lastFrameInputRef = React.createRef();
        this.referenceImagesInputRef = React.createRef();
        this.referenceImageTriggerRef = React.createRef();
        this.referenceImagePopoverRef = React.createRef();
        this.referencePopoverPositionFrame = null;
        this.tutorialDemoBackup = null;

        // 히스토리 진입은 DB snapshot이 유일한 원본입니다.
        // 호출부가 실수로 Starter props를 함께 넘겨도 초기 화면 구성에는 사용하지 않습니다.
        const historyOpen = Boolean(props.initial_history_seq);
        const initialSampleMode = !historyOpen && Boolean(props.initial_sample_mode);
        const initialSampleUserReferenceImage = initialSampleMode
            ? normalizeUserReferenceImageConfig(props.initial_sample_user_reference_image)
            : null;

        this.state = {
            ai_video_studio_history_seq: null,
            selectedHistorySeq: null,
            workflowTitle: '',
            // 마이 스튜디오 갤러리의 특정 과거 RESULT로 연 경우, 저장되기 전 복제 기준으로 사용합니다.
            duplicateSourceResultSeq: null,

            prompt: props.initial_prompt || '',
            samplePrompt: initialSampleMode ? String(props.initial_sample_prompt || '').trim() : '',
            sampleUserReferenceConfig: initialSampleUserReferenceImage,
            sampleUserReferenceDescription: initialSampleMode
                ? String((initialSampleUserReferenceImage && initialSampleUserReferenceImage.image_description) || props.initial_sample_user_reference_description || '').trim()
                : '',
            sampleGuide: initialSampleMode ? {
                title: props.initial_sample_title || '',
                sceneDescription: props.initial_sample_scene_description || '',
                imageGuide: (initialSampleUserReferenceImage && initialSampleUserReferenceImage.image_guide) || props.initial_sample_image_guide || '',
                promptPlaceholder: props.initial_sample_prompt_placeholder || '',
            } : null,
            sampleMetaSnapshot: initialSampleMode ? {
                schema_version: initialSampleUserReferenceImage ? 2 : 1,
                title: props.initial_sample_title || '',
                scene_description: props.initial_sample_scene_description || '',
                image_guide: (initialSampleUserReferenceImage && initialSampleUserReferenceImage.image_guide) || props.initial_sample_image_guide || '',
                user_reference_description: (initialSampleUserReferenceImage && initialSampleUserReferenceImage.image_description) || props.initial_sample_user_reference_description || '',
                user_reference_image: initialSampleUserReferenceImage,
                fixed_reference_images: normalizeReferenceImages(props.initial_reference_images || []),
                initial_options: {
                    video_style_cd: props.initial_video_style_cd || '',
                    camera_angle_cd: props.initial_camera_angle_cd || '',
                    camera_movement_cd: props.initial_camera_movement_cd || '',
                    aspect_ratio: props.initial_aspect_ratio || '9:16',
                    duration_seconds: Number(props.initial_duration_seconds || 8),
                    resolution: props.initial_resolution || '720p',
                },
            } : null,
            sourceImageUrl: props.initial_image_url || props.initial_source_image_url || '',
            sourceImageBase64: '',
            sourceImageFileName: '',
            sourceImageAspectRatio: null,

            referenceImages: normalizeReferenceImages(props.initial_reference_images || []),

            imageSourceModalOpen: false,
            imageSourceTarget: '',
            referenceImagePopoverOpen: false,
            referenceImagePopoverPosition: { top: 0, left: 0, placement: 'bottom', arrowLeft: 32, ready: false },
            showAiImageStudioPopup: false,

            lastFrameImageUrl: props.initial_last_frame_image_url || '',
            lastFrameImageBase64: '',
            lastFrameImageFileName: '',

            videoStyleCd: props.initial_video_style_cd || '',
            cameraAngleCd: props.initial_camera_angle_cd || '',
            cameraMovementCd: props.initial_camera_movement_cd || '',
            aspectRatio: normalizeAspectRatio(props.initial_aspect_ratio || props.aspect_ratio || props.ratio || '9:16'),
            durationSeconds: Number(props.initial_duration_seconds || 8),
            resolution: props.initial_resolution || '720p',

            results: [],
            previewIndex: 0,
            isGenerating: false,
            pollCount: 0,
            // 메타데이터가 준비된 현재 영상 URL. 준비되기 전에는 재생 컨트롤을 노출하지 않습니다.
            videoReadyUrl: '',

            aiUsageLoading: false,
            aiUsage: {
                code: 200,
                total_available_veo_call_count: 0,
                total_used_veo_call_count: 0,
                max_available_veo_call_count: 0,
                is_api_limit_apply_yn: 'N',
            },

            historyItems: [],
            historyMenuSeq: null,
            auto_yn: 'N',
            isMobileHistoryOpen: false,

            historyRenameDialogOpen: false,
            historyRenameTarget: null,
            historyDeleteDialogOpen: false,
            historyDeleteTarget: null,
            historyActionProcessing: false,

            save_dialog_show_state: false,
            save_dialog_content: '',
            save_dialog_button_list: [{ title: '확인', type: '1', display_yn: 'N' }],

            warning_dialog_show_state: false,
            warning_dialog_content: '',
            warning_dialog_button_list: [{ title: '확인', event_method: () => this.setState({ warning_dialog_show_state: false }) }],
            tutorialDemoMode: false,
        };
    }

    componentDidMount() {
        window.addEventListener(STUDIO_TUTORIAL_DEMO_EVENT, this.handleTutorialDemoAction);
        this.loadHistoryList();
        this.loadAiUsage();
        this.updateSourceImageAspectRatio(this.getSourceImagePreview());
        window.addEventListener('resize', this.handleReferencePopoverViewportChange);
        window.addEventListener('scroll', this.handleReferencePopoverViewportChange, true);
        document.addEventListener('mousedown', this.handleReferencePopoverOutsideClick);

        if (this.props.initial_history_seq) {
            this.loadHistoryDetail(this.props.initial_history_seq, {
                useInitialResult: true,
                initialResultSeq: this.props.initial_result_seq || '',
                initialResultNo: this.props.initial_result_no || '',
            });
        } else if (this.isStarterReferenceSample()) {
            this.setState((prev) => ({
                referenceImagePopoverOpen: true,
                referenceImagePopoverPosition: { ...prev.referenceImagePopoverPosition, ready: false },
            }));
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.service_type !== this.props.service_type) {
            this.loadAiUsage();
        }

        const previousSourceImage = prevState.sourceImageBase64 || prevState.sourceImageUrl || '';
        const currentSourceImage = this.getSourceImagePreview();
        if (previousSourceImage !== currentSourceImage) {
            this.updateSourceImageAspectRatio(currentSourceImage);
        }

        if (
            this.state.referenceImagePopoverOpen
            && (!prevState.referenceImagePopoverOpen || prevState.referenceImages !== this.state.referenceImages)
        ) {
            this.scheduleReferenceImagePopoverPositionUpdate();
        }
    }

    componentWillUnmount() {
        window.removeEventListener(STUDIO_TUTORIAL_DEMO_EVENT, this.handleTutorialDemoAction);
        this.tutorialDemoBackup = null;
        this.clearPollTimer();
        window.removeEventListener('resize', this.handleReferencePopoverViewportChange);
        window.removeEventListener('scroll', this.handleReferencePopoverViewportChange, true);
        document.removeEventListener('mousedown', this.handleReferencePopoverOutsideClick);
        if (this.referencePopoverPositionFrame) {
            window.cancelAnimationFrame(this.referencePopoverPositionFrame);
            this.referencePopoverPositionFrame = null;
        }
        if (this.currentSaveTimer) {
            clearTimeout(this.currentSaveTimer);
            this.currentSaveTimer = null;
        }
    }

    handleTutorialDemoAction = (event) => {
        const detail = event && event.detail ? event.detail : {};
        if (String(detail.studio || '').toUpperCase() !== 'VIDEO') return;
        const action = String(detail.action || '').toUpperCase();
        if (action === 'RESTORE') {
            this.restoreTutorialDemoVideoState();
            return;
        }
        if (action === 'SHOW_HISTORY') {
            this.showTutorialHistoryState();
            return;
        }
        if (action === 'HIDE_HISTORY') {
            this.hideTutorialHistoryState();
            return;
        }
        if (action === 'SHOW_RESULTS') {
            this.showTutorialDemoVideoState();
        }
    };

    snapshotTutorialDemoVideoState = () => {
        if (this.tutorialDemoBackup) return;
        this.tutorialDemoBackup = {
            results: this.state.results,
            previewIndex: this.state.previewIndex,
            videoReadyUrl: this.state.videoReadyUrl,
            prompt: this.state.prompt,
            workflowTitle: this.state.workflowTitle,
            isMobileHistoryOpen: this.state.isMobileHistoryOpen,
        };
        this.clearPollTimer();
        if (this.currentSaveTimer) {
            clearTimeout(this.currentSaveTimer);
            this.currentSaveTimer = null;
        }
    };

    showTutorialHistoryState = () => {
        this.snapshotTutorialDemoVideoState();
        if (!this.state.isMobileHistoryOpen) {
            this.setState({ isMobileHistoryOpen: true });
        }
    };

    hideTutorialHistoryState = () => {
        if (this.state.isMobileHistoryOpen) {
            this.setState({ isMobileHistoryOpen: false });
        }
    };

    showTutorialDemoVideoState = () => {
        this.snapshotTutorialDemoVideoState();
        const results = [
            {
                ai_video_studio_result_seq: 'tutorial-demo-1',
                result_no: 1,
                run_status: 'DONE',
                generated_video_url: `${TUTORIAL_DEMO_VIDEO_URL}?result=1`,
                aspect_ratio: '9:16',
                duration_seconds: 8,
                resolution: '720p',
                tutorial_demo_yn: 'Y',
            },
            {
                ai_video_studio_result_seq: 'tutorial-demo-2',
                result_no: 2,
                run_status: 'DONE',
                generated_video_url: `${TUTORIAL_DEMO_VIDEO_URL}?result=2`,
                aspect_ratio: '9:16',
                duration_seconds: 8,
                resolution: '720p',
                tutorial_demo_yn: 'Y',
            },
        ];
        this.setState({
            tutorialDemoMode: true,
            results,
            previewIndex: 0,
            videoReadyUrl: '',
            workflowTitle: '튜토리얼 예시 영상',
        });
    };

    restoreTutorialDemoVideoState = () => {
        if (!this.tutorialDemoBackup) {
            if (this.state.tutorialDemoMode) this.setState({ tutorialDemoMode: false });
            return;
        }
        const backup = this.tutorialDemoBackup;
        this.tutorialDemoBackup = null;
        this.setState({ ...backup, tutorialDemoMode: false });
    };

    isPopupMode = () => this.props.view_mode === 'popup';

    isOpenedFromAiStudioList = () => {
        const openedFrom = this.props.opened_from || this.props.openedFrom || '';
        return openedFrom === 'AI_STUDIO_LIST' || openedFrom === 'AI_VIDEO_STUDIO_LIST';
    };

    getAiResponseType = () => this.props.ai_response_type || 'VIDEO';

    getServiceType = () => {
        const serviceType = this.props.service_type;
        return serviceType && String(serviceType).trim() ? String(serviceType).trim() : '01014';
    };

    loadAiUsage = () => {
        const service_type = this.getServiceType();

        this.setState({
            aiUsageLoading: true,
        });

        return http.get('/rest/shop/ai/usage', {
            params: {
                service_type,
            },
        })
        .then((res) => {
            const data = res.data || {};

            this.setState({
                aiUsageLoading: false,
                aiUsage: {
                    code: data.code || 200,
                    total_available_veo_call_count: data.total_available_veo_call_count || 0,
                    total_used_veo_call_count: data.total_used_veo_call_count || 0,
                    max_available_veo_call_count: data.max_available_veo_call_count || 0,
                    is_api_limit_apply_yn: data.is_api_limit_apply_yn || 'N',
                },
            });
        })
        .catch((e) => {
            console.error('AI 영상 사용량 조회 실패', e);
            this.setState({
                aiUsageLoading: false,
            });
        });
    };

    closeModal = () => {
        this.clearPollTimer();
        if (typeof this.props.onClose === 'function') this.props.onClose();
    };

    openWarning = (message, onConfirm = null) => {
        this.setState({
            warning_dialog_show_state: true,
            warning_dialog_content: message,
            warning_dialog_button_list: [{
                title: '확인',
                event_method: () => {
                    this.setState({
                        warning_dialog_show_state: false,
                        warning_dialog_width_auto: false,
                    }, () => {
                        if (typeof onConfirm === 'function') {
                            onConfirm();
                        }
                    });
                },
            }],
            warning_dialog_width_auto: true,
        });
    };

    toggleMobileHistory = () => this.setState((prev) => ({ isMobileHistoryOpen: !prev.isMobileHistoryOpen }));
    closeMobileHistory = () => this.setState({ isMobileHistoryOpen: false });

    startNewWorkflow = () => {
        this.clearPollTimer();
        this.setState({
            ai_video_studio_history_seq: null,
            selectedHistorySeq: null,
            workflowTitle: '',
            duplicateSourceResultSeq: null,
            prompt: '',
            samplePrompt: '',
            sampleUserReferenceConfig: null,
            sampleUserReferenceDescription: '',
            sampleGuide: null,
            sampleMetaSnapshot: null,
            sourceImageUrl: '',
            sourceImageBase64: '',
            sourceImageFileName: '',
            sourceImageAspectRatio: null,
            referenceImages: [],
            imageSourceModalOpen: false,
            imageSourceTarget: '',
            referenceImagePopoverOpen: false,
            showAiImageStudioPopup: false,
            lastFrameImageUrl: '',
            lastFrameImageBase64: '',
            lastFrameImageFileName: '',
            videoStyleCd: '',
            cameraAngleCd: '',
            cameraMovementCd: '',
            aspectRatio: normalizeAspectRatio(this.props.aspect_ratio || this.props.ratio || '9:16'),
            durationSeconds: 8,
            resolution: '720p',
            results: [],
            previewIndex: 0,
            isGenerating: false,
            pollCount: 0,
            historyMenuSeq: null,
            isMobileHistoryOpen: false,
        });
    };

    handlePromptChange = (event) => {
        // const prompt = event.target.value.slice(0, PROMPT_MAX_LENGTH);
        this.setState({ prompt: event.target.value }, this.scheduleCurrentSave);
    };

    setOption = (key, value) => {
        const patch = { [key]: value };
        if (key === 'resolution' && value !== '720p') {
            patch.durationSeconds = 8;
        }
        if (key === 'durationSeconds' && this.hasReferenceImages()) {
            patch.durationSeconds = 8;
        }
        this.setState(patch, this.scheduleCurrentSave);
    };

    updateSourceImageAspectRatio = (imageSrc) => {
        const source = String(imageSrc || '').trim();
        if (!source) {
            if (this.state.sourceImageAspectRatio !== null) {
                this.setState({ sourceImageAspectRatio: null });
            }
            return;
        }

        const image = new Image();
        image.onload = () => {
            if (this.getSourceImagePreview() !== source) return;
            const width = Number(image.naturalWidth || image.width || 0);
            const height = Number(image.naturalHeight || image.height || 0);
            const sourceImageAspectRatio = width > 0 && height > 0 ? width / height : null;
            this.setState({ sourceImageAspectRatio });
        };
        image.onerror = () => {
            if (this.getSourceImagePreview() === source) {
                this.setState({ sourceImageAspectRatio: null });
            }
        };
        image.src = source;
    };

    isSourceImageAspectRatioMismatch = () => {
        const sourceRatio = Number(this.state.sourceImageAspectRatio || 0);
        if (!sourceRatio) return false;

        const targetRatio = normalizeAspectRatio(this.state.aspectRatio) === '16:9'
            ? 16 / 9
            : 9 / 16;

        // 이미지 메타데이터의 1~2px 오차는 같은 비율로 판단합니다.
        return Math.abs(sourceRatio - targetRatio) > 0.01;
    };

    triggerSourceImageInput = () => {
        if (this.hasReferenceImages()) {
            this.openWarning('참조 이미지와 시작 이미지는 동시에 사용할 수 없습니다. 참조 이미지를 먼저 삭제해 주세요.');
            return;
        }
        if (this.fileInputRef.current) {
            this.fileInputRef.current.value = '';
            this.fileInputRef.current.click();
        }
    };

    handleSourceImageChange = (event) => {
        if (this.hasReferenceImages()) {
            this.openWarning('참조 이미지와 시작 이미지는 동시에 사용할 수 없습니다.');
            return;
        }
        const file = event.target.files && event.target.files[0];
        if (!file) return;
        if (!String(file.type || '').startsWith('image/')) {
            this.openWarning('시작 이미지는 이미지 파일만 사용할 수 있습니다.');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            this.setState({
                sourceImageBase64: e.target && e.target.result ? String(e.target.result) : '',
                sourceImageUrl: '',
                sourceImageFileName: file.name || '시작 이미지',
                imageSourceTarget: '',
            }, this.scheduleCurrentSave);
        };
        reader.readAsDataURL(file);
    };

    removeSourceImage = () => {
        this.setState({
            sourceImageUrl: '',
            sourceImageBase64: '',
            sourceImageFileName: '',
            sourceImageAspectRatio: null,
            lastFrameImageUrl: '',
            lastFrameImageBase64: '',
            lastFrameImageFileName: '',
        }, this.scheduleCurrentSave);
    };

    getSourceImagePreview = () => this.state.sourceImageBase64 || this.state.sourceImageUrl || '';

    openImageSourceModal = (target) => {
        const normalizedTarget = String(target || '').toUpperCase();
        const hasSourceImage = !!this.getSourceImagePreview();
        const hasLastFrameImage = !!this.getLastFrameImagePreview();
        const referenceImages = this.state.referenceImages || [];

        if (normalizedTarget === 'SOURCE' && referenceImages.length) {
            this.openWarning('시작 이미지는 참조 이미지와 함께 사용할 수 없습니다. 참조 이미지를 먼저 삭제해 주세요.');
            return;
        }
        if (normalizedTarget === 'REFERENCE' && (hasSourceImage || hasLastFrameImage)) {
            this.openWarning('참조 이미지는 시작 이미지와 함께 사용할 수 없습니다. 시작 이미지를 먼저 삭제해 주세요.');
            return;
        }
        if (normalizedTarget === 'REFERENCE' && referenceImages.length >= this.getReferenceImageMaxCount()) {
            this.setState((prev) => ({
                referenceImagePopoverOpen: true,
                referenceImagePopoverPosition: { ...prev.referenceImagePopoverPosition, ready: false },
            }));
            return;
        }

        this.setState({
            imageSourceModalOpen: true,
            imageSourceTarget: normalizedTarget,
            referenceImagePopoverOpen: false,
        });
    };

    closeImageSourceModal = () => this.setState({ imageSourceModalOpen: false, imageSourceTarget: '' });

    handleImageSourceMethod = (method) => {
        const target = this.state.imageSourceTarget;
        if (method === 'UPLOAD') {
            this.setState({ imageSourceModalOpen: false, imageSourceTarget: '' }, () => {
                if (target === 'SOURCE') this.triggerSourceImageInput();
                if (target === 'REFERENCE') this.triggerReferenceImagesInput();
            });
            return;
        }

        if (method === 'STUDIO') {
            this.setState({
                imageSourceModalOpen: false,
                showAiImageStudioPopup: true,
            });
        }
    };

    closeAiImageStudioPopup = () => this.setState({
        showAiImageStudioPopup: false,
        imageSourceTarget: '',
    });

    handleAiImageStudioApply = (result) => {
        const appliedImage = firstNonEmpty(
            result && result.appliedImage,
            result && result.appliedValue,
            result && result.output_image_url,
            result && result.image_url
        );
        if (!appliedImage) {
            this.openWarning('이미지 스튜디오에서 적용할 이미지를 선택해 주세요.');
            return;
        }

        const imageValue = String(appliedImage);
        const isBase64 = /^data:image\//i.test(imageValue);
        const mimeMatch = imageValue.match(/^data:([^;]+);base64,/i);

        if (this.state.imageSourceTarget === 'SOURCE') {
            if (this.hasReferenceImages()) {
                this.openWarning('시작 이미지는 참조 이미지와 함께 사용할 수 없습니다.');
                return;
            }
            this.setState({
                sourceImageUrl: isBase64 ? '' : imageValue,
                sourceImageBase64: isBase64 ? imageValue : '',
                sourceImageFileName: '이미지 스튜디오 이미지',
                showAiImageStudioPopup: false,
                imageSourceTarget: '',
            }, this.scheduleCurrentSave);
            return;
        }

        if (this.state.imageSourceTarget === 'REFERENCE') {
            if (this.getSourceImagePreview() || this.getLastFrameImagePreview()) {
                this.openWarning('참조 이미지는 시작 이미지와 함께 사용할 수 없습니다.');
                return;
            }
            const referenceImageMaxCount = this.getReferenceImageMaxCount();
            if ((this.state.referenceImages || []).length >= referenceImageMaxCount) {
                this.openWarning(this.isStarterReferenceSample()
                    ? '이 샘플에는 준비할 이미지 1장만 추가할 수 있습니다.'
                    : '참조 이미지는 최대 3장까지 사용할 수 있습니다.');
                return;
            }
            this.setState((prev) => ({
                referenceImages: [
                    ...(prev.referenceImages || []),
                    {
                        id: `reference-studio-${Date.now()}-${Math.random()}`,
                        imageUrl: isBase64 ? '' : imageValue,
                        imageBase64: isBase64 ? imageValue : '',
                        mimeType: mimeMatch && mimeMatch[1] ? mimeMatch[1] : 'image/png',
                        fileName: `참조 이미지 ${(prev.referenceImages || []).length + 1}`,
                        description: '',
                    },
                ].slice(0, referenceImageMaxCount),
                durationSeconds: 8,
                showAiImageStudioPopup: false,
                imageSourceTarget: '',
                referenceImagePopoverOpen: true,
                referenceImagePopoverPosition: { ...prev.referenceImagePopoverPosition, ready: false },
            }), this.scheduleCurrentSave);
        }
    };

    scheduleReferenceImagePopoverPositionUpdate = () => {
        if (typeof window === 'undefined') return;
        if (this.referencePopoverPositionFrame) {
            window.cancelAnimationFrame(this.referencePopoverPositionFrame);
        }
        this.referencePopoverPositionFrame = window.requestAnimationFrame(() => {
            this.referencePopoverPositionFrame = null;
            this.updateReferenceImagePopoverPosition();
        });
    };

    updateReferenceImagePopoverPosition = () => {
        if (!this.state.referenceImagePopoverOpen || typeof window === 'undefined') return;

        const trigger = this.referenceImageTriggerRef.current;
        const popover = this.referenceImagePopoverRef.current;
        if (!trigger || !popover) return;

        const triggerRect = trigger.getBoundingClientRect();
        if (
            triggerRect.bottom < 0
            || triggerRect.top > window.innerHeight
            || triggerRect.right < 0
            || triggerRect.left > window.innerWidth
        ) {
            this.closeReferenceImagePopover();
            return;
        }

        const popoverWidth = popover.offsetWidth || Math.min(620, window.innerWidth - 24);
        const popoverHeight = popover.offsetHeight || 360;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const edgeMargin = 12;
        const triggerGap = 11;

        let left = triggerRect.left + (triggerRect.width / 2) - (popoverWidth / 2);
        left = Math.max(edgeMargin, Math.min(left, viewportWidth - popoverWidth - edgeMargin));

        const availableBelow = viewportHeight - triggerRect.bottom - triggerGap - edgeMargin;
        const availableAbove = triggerRect.top - triggerGap - edgeMargin;
        const placement = availableBelow >= popoverHeight || availableBelow >= availableAbove ? 'bottom' : 'top';

        let top = placement === 'bottom'
            ? triggerRect.bottom + triggerGap
            : triggerRect.top - popoverHeight - triggerGap;
        top = Math.max(edgeMargin, Math.min(top, viewportHeight - popoverHeight - edgeMargin));

        const arrowLeft = Math.max(22, Math.min(
            triggerRect.left + (triggerRect.width / 2) - left,
            popoverWidth - 22
        ));

        const nextPosition = { top, left, placement, arrowLeft, ready: true };
        const currentPosition = this.state.referenceImagePopoverPosition || {};
        if (
            Math.abs((currentPosition.top || 0) - top) < 1
            && Math.abs((currentPosition.left || 0) - left) < 1
            && currentPosition.placement === placement
            && Math.abs((currentPosition.arrowLeft || 0) - arrowLeft) < 1
            && currentPosition.ready
        ) return;

        this.setState({ referenceImagePopoverPosition: nextPosition });
    };

    handleReferencePopoverViewportChange = () => {
        if (this.state.referenceImagePopoverOpen) {
            this.scheduleReferenceImagePopoverPositionUpdate();
        }
    };

    handleReferencePopoverOutsideClick = (event) => {
        if (!this.state.referenceImagePopoverOpen) return;
        const trigger = this.referenceImageTriggerRef.current;
        const popover = this.referenceImagePopoverRef.current;
        if (trigger && trigger.contains(event.target)) return;
        if (popover && popover.contains(event.target)) return;
        this.closeReferenceImagePopover();
    };

    toggleReferenceImagePopover = () => {
        if (this.getSourceImagePreview() || this.getLastFrameImagePreview()) return;
        if (!(this.state.referenceImages || []).length) {
            this.openImageSourceModal('REFERENCE');
            return;
        }
        this.setState((prev) => ({
            referenceImagePopoverOpen: !prev.referenceImagePopoverOpen,
            referenceImagePopoverPosition: !prev.referenceImagePopoverOpen
                ? { ...prev.referenceImagePopoverPosition, ready: false }
                : prev.referenceImagePopoverPosition,
        }));
    };

    closeReferenceImagePopover = () => this.setState({ referenceImagePopoverOpen: false });

    renderImageAddIcon = () => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M6.19739 20.9343L15.256 12.4086L19.5188 16.6714M6.19739 20.9343H16.8545C18.6203 20.9343 20.0517 19.5029 20.0517 17.7371V12.4086M6.19739 20.9343C4.43165 20.9343 3.00024 19.5029 3.00024 17.7371V7.08001C3.00024 5.31427 4.43165 3.88287 6.19739 3.88287H13.1245M18.986 9.02859V6.01429M18.986 6.01429V3M18.986 6.01429H15.9717M18.986 6.01429H22.0002M9.39453 8.67858C9.39453 9.56144 8.67882 10.2771 7.79596 10.2771C6.91309 10.2771 6.19739 9.56144 6.19739 8.67858C6.19739 7.79571 6.91309 7.08001 7.79596 7.08001C8.67882 7.08001 9.39453 7.79571 9.39453 8.67858Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    renderFolderIcon = () => (
        <svg width="35" height="35" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M2 20.5V4.5C2 3.67157 2.67157 3 3.5 3H8.25L10.5 6H19.5C20.3284 6 21 6.67157 21 7.5V11.25" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M2.25 20.5L5.4 12.9C5.74944 12.0576 6.57153 11.5 7.48352 11.5H21.0986C22.4542 11.5 23.3246 12.9384 22.7044 14.1439L19.3978 20.5714C19.1397 21.0732 18.6227 21.3889 18.0584 21.3889H3.1724C2.5197 21.3889 2.00008 20.8483 2.02591 20.1961C2.03008 20.0908 2.05297 19.9871 2.09342 19.8898L2.25 20.5Z" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    renderImageStudioIcon = () => (
        <svg width="35" height="35" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <g clipPath="url(#aiVideoImageStudioIconClip)">
                <path d="M10.5254 18.9337C11.0404 18.9337 11.458 18.5135 11.458 17.9953C11.458 17.477 11.0404 17.0569 10.5254 17.0569C10.0103 17.0569 9.59277 17.477 9.59277 17.9953C9.59277 18.5135 10.0103 18.9337 10.5254 18.9337Z" fill="#333333" />
                <path d="M16.5674 18.9337C17.0824 18.9337 17.5 18.5135 17.5 17.9953C17.5 17.477 17.0824 17.0569 16.5674 17.0569C16.0523 17.0569 15.6348 17.477 15.6348 17.9953C15.6348 18.5135 16.0523 18.9337 16.5674 18.9337Z" fill="#333333" />
                <path d="M16.8785 23H10.2386C7.90312 23 5.99805 21.0831 5.99805 18.7331V17.9231C5.99805 15.5731 7.90312 13.6562 10.2386 13.6562H16.8785C19.214 13.6562 21.1191 15.5731 21.1191 17.9231V18.7331C21.1191 21.0831 19.214 23 16.8785 23ZM10.2386 15.172C8.73211 15.172 7.51254 16.4072 7.51254 17.915V18.7251C7.51254 20.241 8.73211 21.4681 10.2386 21.4681H16.8785C18.385 21.4681 19.6046 20.241 19.6046 18.7251V17.915C19.6046 16.3992 18.377 15.172 16.8785 15.172H10.2386Z" fill="#333333" />
                <path d="M14.1203 12.1243H13.0044V14.0813H14.1203V12.1243Z" fill="#333333" />
                <path d="M13.5625 20.658C12.9408 20.658 12.3589 20.4094 12.2951 20.3773C12.056 20.273 11.9444 19.9923 12.048 19.7437C12.1516 19.5031 12.4386 19.3908 12.6777 19.4951C12.6777 19.4951 13.14 19.6956 13.5705 19.6956C14.0009 19.6956 14.4553 19.5031 14.4632 19.4951C14.7024 19.3908 14.9893 19.4951 15.0929 19.7437C15.1966 19.9843 15.0929 20.273 14.8458 20.3773C14.7821 20.4094 14.1922 20.658 13.5785 20.658H13.5625Z" fill="#333333" />
                <path d="M13.5622 12.7178C14.3282 12.7178 14.9492 12.093 14.9492 11.3222C14.9492 10.5515 14.3282 9.9267 13.5622 9.9267C12.7963 9.9267 12.1753 10.5515 12.1753 11.3222C12.1753 12.093 12.7963 12.7178 13.5622 12.7178Z" fill="#333333" />
                <path d="M21.5494 20.7142C21.3502 20.7142 21.1589 20.6981 20.9835 20.6821L20.2422 20.634V15.934H20.8002C21.4618 15.934 23.0002 15.934 23.0002 17.2494V19.5673C23.0002 19.9763 22.8168 20.2249 22.6733 20.3613C22.3704 20.642 21.956 20.7062 21.5494 20.7062V20.7142ZM21.3581 19.5833C21.5813 19.5994 21.8364 19.5913 21.9241 19.5432L21.8922 17.2574C21.8284 17.1611 21.6052 17.113 21.3661 17.089V19.5913L21.3581 19.5833Z" fill="#333333" />
                <path d="M5.76664 20.7142C5.36012 20.7142 4.94563 20.65 4.64273 20.3693C4.49128 20.2329 4.31592 19.9763 4.31592 19.5753V17.2574C4.31592 15.942 5.85432 15.942 6.51592 15.942H7.07389V20.634L6.33258 20.6821C6.15722 20.6981 5.96592 20.7142 5.76664 20.7142ZM5.95795 17.0809C5.71882 17.097 5.49563 17.1371 5.43983 17.2092V19.5753C5.49563 19.6073 5.74273 19.5993 5.95795 19.5833V17.0809Z" fill="#333333" />
                <path d="M6.76285 5.87641C6.97806 6.09297 7.27299 6.21327 7.57589 6.21327C7.87879 6.21327 8.16575 6.09297 8.38096 5.87641C8.59618 5.65986 8.71575 5.37113 8.71575 5.06635C8.71575 4.76158 8.59618 4.47284 8.38096 4.25629C8.16575 4.03974 7.87879 3.91943 7.57589 3.91943C7.27299 3.91943 6.98603 4.03974 6.77082 4.25629C6.5556 4.47284 6.43604 4.76158 6.43604 5.06635C6.43604 5.21874 6.46792 5.37113 6.52372 5.50748C6.57951 5.64382 6.66719 5.77215 6.77082 5.88444L6.76285 5.87641Z" fill="#333333" />
                <path d="M2.52246 17.8589V11.4987L5.56739 8.43493L7.53623 10.416C7.82319 10.6967 8.2058 10.8571 8.61232 10.8571C9.01884 10.8571 9.40145 10.6967 9.68841 10.416L13.9449 6.13307L17.7551 9.96682V12.8542C17.7551 13.6081 19.2775 13.6482 19.2775 12.8782V2.5319C19.2775 2.12286 19.1181 1.73788 18.8312 1.44914C18.5442 1.16041 18.1616 1 17.7551 1H2.52246C2.11594 1 1.73333 1.16041 1.44638 1.44914C1.15942 1.73788 1 2.12286 1 2.5319V17.8589C1 18.268 1.15942 18.6529 1.44638 18.9417C1.73333 19.2304 2.11594 19.3908 2.52246 19.3908H2.92101C3.86159 19.3908 3.82971 17.8669 2.96884 17.8669C2.79348 17.8669 2.52246 17.8669 2.52246 17.8669V17.8589ZM2.52246 2.5319H17.7551V7.80131L15.021 5.05031C14.7341 4.7696 14.3514 4.60919 13.9449 4.60919C13.5384 4.60919 13.1558 4.7696 12.8688 5.05031L8.61232 9.33321L6.64348 7.35217C6.35652 7.07145 5.97391 6.91105 5.56739 6.91105C5.16087 6.91105 4.77826 6.91105 4.4913 7.35217L2.52246 9.33321V2.5319Z" fill="#333333" />
            </g>
            <defs><clipPath id="aiVideoImageStudioIconClip"><rect width="22" height="22" fill="white" transform="translate(1 1)" /></clipPath></defs>
        </svg>
    );

    hasReferenceImages = () => Array.isArray(this.state.referenceImages) && this.state.referenceImages.length > 0;

    getStarterUserReferenceConfig = () => {
        const current = normalizeUserReferenceImageConfig(this.state.sampleUserReferenceConfig);
        if (current) return current;
        const stored = this.state.sampleMetaSnapshot || {};
        return normalizeUserReferenceImageConfig(stored.user_reference_image || stored.userReferenceImage || null);
    };

    isStarterReferenceSample = () => Boolean(
        String(this.state.samplePrompt || '').trim()
        && this.getStarterUserReferenceConfig()
    );

    getStarterFixedReferenceCount = () => {
        if (!this.isStarterReferenceSample()) return 0;
        const stored = this.state.sampleMetaSnapshot || {};
        return normalizeReferenceImages(stored.fixed_reference_images || stored.fixedReferenceImages || []).length;
    };

    getReferenceImageMaxCount = () => {
        if (!this.isStarterReferenceSample()) return MAX_REFERENCE_IMAGE_COUNT;
        const config = this.getStarterUserReferenceConfig();
        return Math.min(MAX_REFERENCE_IMAGE_COUNT, this.getStarterFixedReferenceCount() + Number(config.max_count || 1));
    };

    getStarterUserReferenceImages = () => {
        if (!this.isStarterReferenceSample()) return [];
        const config = this.getStarterUserReferenceConfig();
        const fixedCount = this.getStarterFixedReferenceCount();
        return (this.state.referenceImages || []).slice(fixedCount, fixedCount + Number(config.max_count || 1));
    };

    getStarterReferenceUi = () => {
        const config = this.getStarterUserReferenceConfig();
        if (!config) return null;
        return {
            addLabel: config.add_label || '이미지 추가',
            infoLabel: config.slot_label || '참조 이미지',
            missingImageMessage: config.image_validation_message || '참조 이미지를 추가해 주세요.',
            missingFieldMessage: config.field_validation_message || '',
            inputFields: config.input_fields || [],
        };
    };

    openStarterReferencePopover = () => this.setState((prev) => ({
        referenceImagePopoverOpen: true,
        referenceImagePopoverPosition: { ...prev.referenceImagePopoverPosition, ready: false },
    }));

    changeStarterReferenceField = (index, field, value) => {
        if (!field || !field.key) return;
        const maxLength = Math.max(1, Number(field.max_length || MAX_REFERENCE_DESCRIPTION_LENGTH));
        const nextValue = String(value || '').slice(0, maxLength);
        const config = this.getStarterUserReferenceConfig();
        this.setState((prev) => ({
            referenceImages: (prev.referenceImages || []).map((item, itemIndex) => {
                if (itemIndex !== index) return item;
                return {
                    ...item,
                    inputValues: { ...getReferenceInputValues(config, item), [field.key]: nextValue },
                };
            }),
        }), this.scheduleCurrentSave);
    };

    validateStarterReferenceInput = () => {
        if (!this.isStarterReferenceSample()) return true;
        const config = this.getStarterUserReferenceConfig();
        const ui = this.getStarterReferenceUi();
        const userReferences = this.getStarterUserReferenceImages();
        if (userReferences.length < Number(config.min_count || 0)) {
            // this.openStarterReferencePopover();
            this.openWarning(ui.missingImageMessage, this.openStarterReferencePopover);
            return false;
        }
        const requiredFields = (config.input_fields || []).filter((field) => field.required !== false);
        for (let i = 0; i < userReferences.length; i += 1) {
            const values = getReferenceInputValues(config, userReferences[i]);
            const missingField = requiredFields.find((field) => !String(values[field.key] || '').trim());
            if (missingField) {
                // this.openStarterReferencePopover();
                const fallbackMessage = requiredFields.length > 1
                    ? `${requiredFields.map((field) => field.label).join(', ')}을 모두 입력해 주세요.`
                    : `${missingField.label || '정보'}을 입력해 주세요.`;
                this.openWarning((ui.missingFieldMessage || fallbackMessage), this.openStarterReferencePopover);
                return false;
            }
        }
        return true;
    };

    getReferenceImagesPayload = () => {
        const starterReferenceSample = this.isStarterReferenceSample();
        const fixedReferenceCount = starterReferenceSample ? this.getStarterFixedReferenceCount() : 0;
        const config = starterReferenceSample ? this.getStarterUserReferenceConfig() : null;
        const userReferenceMaxCount = config ? Number(config.max_count || 1) : 0;
        return (this.state.referenceImages || []).map((item, index) => {
            const starterUserReference = starterReferenceSample
                && index >= fixedReferenceCount
                && index < fixedReferenceCount + userReferenceMaxCount;
            const description = starterUserReference
                ? formatReferenceDescription(config, item)
                : String(item.description || '').trim();
            return {
                image_url: item.imageUrl || '',
                image_base64: item.imageBase64 || '',
                image_mime_type: item.mimeType || 'image/png',
                file_name: item.fileName || '',
                description,
            };
        });
    };

    triggerReferenceImagesInput = () => {
        if (this.getSourceImagePreview() || this.getLastFrameImagePreview()) {
            this.openWarning('참조 이미지는 시작 이미지 또는 마지막 이미지와 동시에 사용할 수 없습니다. 기존 프레임 이미지를 먼저 삭제해 주세요.');
            return;
        }
        const referenceImageMaxCount = this.getReferenceImageMaxCount();
        if ((this.state.referenceImages || []).length >= referenceImageMaxCount) {
            this.openWarning(this.isStarterReferenceSample()
                ? '이 샘플에 필요한 이미지는 모두 추가되었습니다.'
                : '참조 이미지는 최대 3장까지 사용할 수 있습니다.');
            return;
        }
        if (this.referenceImagesInputRef.current) {
            this.referenceImagesInputRef.current.value = '';
            this.referenceImagesInputRef.current.click();
        }
    };

    readImageFileAsDataUrl = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target && event.target.result ? String(event.target.result) : '');
        reader.onerror = () => reject(new Error('이미지 파일을 읽을 수 없습니다.'));
        reader.readAsDataURL(file);
    });

    handleReferenceImagesChange = async (event) => {
        const files = Array.from((event.target && event.target.files) || []);
        if (!files.length) return;
        if (this.getSourceImagePreview() || this.getLastFrameImagePreview()) {
            this.openWarning('참조 이미지는 시작 이미지 또는 마지막 이미지와 동시에 사용할 수 없습니다.');
            return;
        }

        const invalid = files.find((file) => !String(file.type || '').startsWith('image/'));
        if (invalid) {
            this.openWarning('참조 이미지는 이미지 파일만 사용할 수 있습니다.');
            return;
        }

        const referenceImageMaxCount = this.getReferenceImageMaxCount();
        const remaining = referenceImageMaxCount - (this.state.referenceImages || []).length;
        if (remaining <= 0) {
            this.openWarning(this.isStarterReferenceSample()
                ? '이 샘플에 필요한 이미지는 모두 추가되었습니다.'
                : '참조 이미지는 최대 3장까지 사용할 수 있습니다.');
            return;
        }
        const selectedFiles = files.slice(0, remaining);

        try {
            const added = await Promise.all(selectedFiles.map(async (file, index) => ({
                id: `reference-${Date.now()}-${index}-${Math.random()}`,
                imageUrl: '',
                imageBase64: await this.readImageFileAsDataUrl(file),
                mimeType: file.type || 'image/png',
                fileName: file.name || `참조 이미지 ${(this.state.referenceImages || []).length + index + 1}`,
                description: '',
                inputValues: {},
            })));

            this.setState((prev) => ({
                referenceImages: [...(prev.referenceImages || []), ...added].slice(0, referenceImageMaxCount),
                durationSeconds: 8,
                referenceImagePopoverOpen: true,
                referenceImagePopoverPosition: { ...prev.referenceImagePopoverPosition, ready: false },
            }), this.scheduleCurrentSave);

            if (files.length > selectedFiles.length) {
                this.openWarning(this.isStarterReferenceSample()
                    ? '이 샘플에 필요한 이미지 수만큼만 추가되었습니다.'
                    : '참조 이미지는 최대 3장까지만 추가되었습니다.');
            }
        } catch (error) {
            this.openWarning(error?.message || '참조 이미지를 불러오지 못했습니다.');
        }
    };

    removeReferenceImage = (index) => {
        this.setState((prev) => ({
            referenceImages: (prev.referenceImages || []).filter((item, itemIndex) => itemIndex !== index),
        }), this.scheduleCurrentSave);
    };

    changeReferenceImageDescription = (index, value) => {
        const description = String(value || '').slice(0, MAX_REFERENCE_DESCRIPTION_LENGTH);
        this.setState((prev) => ({
            referenceImages: (prev.referenceImages || []).map((item, itemIndex) => (
                itemIndex === index ? { ...item, description } : item
            )),
        }), this.scheduleCurrentSave);
    };

    triggerLastFrameImageInput = () => {
        if (this.hasReferenceImages()) {
            this.openWarning('참조 이미지와 마지막 이미지는 동시에 사용할 수 없습니다. 참조 이미지를 먼저 삭제해 주세요.');
            return;
        }
        if (!this.getSourceImagePreview()) {
            this.openWarning('마지막 이미지는 시작 이미지와 함께 사용할 수 있습니다. 시작 이미지를 먼저 추가해 주세요.');
            return;
        }
        if (this.lastFrameInputRef.current) {
            this.lastFrameInputRef.current.value = '';
            this.lastFrameInputRef.current.click();
        }
    };

    handleLastFrameImageChange = (event) => {
        const file = event.target.files && event.target.files[0];
        if (!file) return;
        if (!String(file.type || '').startsWith('image/')) {
            this.openWarning('마지막 이미지는 이미지 파일만 사용할 수 있습니다.');
            return;
        }
        if (!this.getSourceImagePreview()) {
            this.openWarning('마지막 이미지는 시작 이미지와 함께 사용할 수 있습니다. 시작 이미지를 먼저 추가해 주세요.');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            this.setState({
                lastFrameImageBase64: e.target && e.target.result ? String(e.target.result) : '',
                lastFrameImageUrl: '',
                lastFrameImageFileName: file.name || '마지막 이미지',
                durationSeconds: 8,
            }, this.scheduleCurrentSave);
        };
        reader.readAsDataURL(file);
    };

    removeLastFrameImage = () => {
        this.setState({
            lastFrameImageUrl: '',
            lastFrameImageBase64: '',
            lastFrameImageFileName: '',
        }, this.scheduleCurrentSave);
    };

    getLastFrameImagePreview = () => this.state.lastFrameImageBase64 || this.state.lastFrameImageUrl || '';

    parseStarterMetaSnapshot = (rawMeta) => {
        if (!rawMeta) return null;
        if (typeof rawMeta === 'object') return rawMeta;
        try {
            const parsed = JSON.parse(String(rawMeta));
            return parsed && typeof parsed === 'object' ? parsed : null;
        } catch (e) {
            console.warn('Starter 샘플 메타데이터 파싱 실패', e);
            return null;
        }
    };

    buildStarterMetaSnapshot = () => {
        const guide = this.state.sampleGuide || {};
        const stored = this.state.sampleMetaSnapshot || {};
        return {
            ...stored,
            schema_version: Math.max(Number(stored.schema_version) || 1, this.getStarterUserReferenceConfig() ? 2 : 1),
            title: guide.title || stored.title || '',
            scene_description: guide.sceneDescription || stored.scene_description || '',
            image_guide: guide.imageGuide || stored.image_guide || '',
            user_reference_description: this.state.sampleUserReferenceDescription || stored.user_reference_description || '',
            user_reference_image: this.getStarterUserReferenceConfig() || stored.user_reference_image || null,
        };
    };

    buildRequestBody = () => ({
        ai_video_studio_history_seq: this.state.ai_video_studio_history_seq || null,
        history_title: this.state.workflowTitle || (this.state.sampleGuide && this.state.sampleGuide.title) || '',
        starter_sample_yn: this.state.samplePrompt ? 'Y' : 'N',
        starter_prompt_snapshot: this.state.samplePrompt || '',
        starter_meta_snapshot_json: this.state.samplePrompt ? JSON.stringify(this.buildStarterMetaSnapshot()) : '',
        prompt_text: String(this.state.prompt || '').trim(),
        source_image_url: this.state.sourceImageUrl || '',
        source_image_base64: this.state.sourceImageBase64 || '',
        source_image_mime_type: this.getSourceImageMimeType(),
        reference_images: this.getReferenceImagesPayload(),
        last_frame_image_url: this.state.lastFrameImageUrl || '',
        last_frame_image_base64: this.state.lastFrameImageBase64 || '',
        last_frame_image_mime_type: this.getLastFrameImageMimeType(),
        video_style_cd: this.state.videoStyleCd || '',
        camera_angle_cd: this.state.cameraAngleCd || '',
        camera_movement_cd: this.state.cameraMovementCd || '',
        aspect_ratio: this.state.aspectRatio || '9:16',
        duration_seconds: Number(this.state.durationSeconds || 8),
        resolution: this.state.resolution || '720p',
        auto_yn: this.state.auto_yn || 'N',
    });

    getSourceImageMimeType = () => {
        const dataUrl = this.state.sourceImageBase64 || '';
        const match = String(dataUrl).match(/^data:([^;]+);base64,/i);
        return match && match[1] ? match[1] : 'image/png';
    };

    getLastFrameImageMimeType = () => {
        const dataUrl = this.state.lastFrameImageBase64 || '';
        const match = String(dataUrl).match(/^data:([^;]+);base64,/i);
        return match && match[1] ? match[1] : 'image/png';
    };

    generateVideo = async () => {
        if (this.state.tutorialDemoMode) return;
        const prompt = String(this.state.prompt || '').trim();
        const executablePrompt = String(this.state.samplePrompt || '').trim() || prompt;
        if (!executablePrompt) {
            this.openWarning('생성할 영상에 대한 설명을 입력해 주세요.');
            return;
        }
        if (!this.validateStarterReferenceInput()) {
            return;
        }
        if (this.hasReferenceImages() && (this.getSourceImagePreview() || this.getLastFrameImagePreview())) {
            this.openWarning('참조 이미지는 시작 이미지 또는 마지막 이미지와 동시에 사용할 수 없습니다.');
            return;
        }
        if (this.getLastFrameImagePreview() && !this.getSourceImagePreview()) {
            this.openWarning('마지막 이미지는 시작 이미지와 함께 사용해야 합니다.');
            return;
        }
        if (this.state.isGenerating) return;

        this.clearPollTimer();
        this.setState({
            isGenerating: true,
            pollCount: 0,
            save_dialog_show_state: true,
            save_dialog_content: 'AI 영상 생성을 요청중입니다...',
            save_dialog_button_list: [{ title: '확인', type: '1', display_yn: 'N' }],
        });

        try {
            const response = await http.post('/rest/shop/aiVideoStudio/generate', this.buildRequestBody(), {
                headers: { 'Content-Type': 'application/json' },
            });
            const data = response && response.data ? response.data : {};
            if (data.result !== 'success') {
                throw new Error(data.message || '영상 생성 요청에 실패했습니다.');
            }

            const nextResult = {
                ai_video_studio_result_seq: data.ai_video_studio_result_seq,
                ai_video_studio_history_seq: data.ai_video_studio_history_seq,
                result_no: data.result_no,
                prompt_text: data.prompt_text || prompt,
                request_prompt_text: data.request_prompt_text || '',
                source_image_url: data.source_image_url || this.state.sourceImageUrl || '',
                reference_images: normalizeReferenceImages(data.reference_images || this.state.referenceImages),
                last_frame_image_url: data.last_frame_image_url || this.state.lastFrameImageUrl || '',
                video_style_cd: data.video_style_cd || this.state.videoStyleCd || '',
                camera_angle_cd: data.camera_angle_cd || this.state.cameraAngleCd || '',
                camera_movement_cd: data.camera_movement_cd || this.state.cameraMovementCd || '',
                aspect_ratio: data.aspect_ratio || this.state.aspectRatio || '9:16',
                duration_seconds: data.duration_seconds || this.state.durationSeconds || 8,
                resolution: data.resolution || this.state.resolution || '720p',
                operation_name: data.operation_name || '',
                generated_video_url: '',
                generated_thumbnail_url: '',
                run_status: 'PROCESSING',
                error_message: '',
            };

            await new Promise((resolve) => {
                this.setState((prev) => ({
                    ai_video_studio_history_seq: data.ai_video_studio_history_seq,
                    selectedHistorySeq: data.ai_video_studio_history_seq,
                    workflowTitle: prev.workflowTitle || this.makeHistoryTitle(prompt),
                    // /generate가 HISTORY.current_*를 현재 화면 값으로 갱신했으므로 이후에는 current 기준으로 복제합니다.
                    duplicateSourceResultSeq: null,
                    sourceImageUrl: data.source_image_url || prev.sourceImageUrl,
                    sourceImageBase64: data.source_image_url ? '' : prev.sourceImageBase64,
                    referenceImages: data.reference_images ? normalizeReferenceImages(data.reference_images) : prev.referenceImages,
                    lastFrameImageUrl: data.last_frame_image_url || prev.lastFrameImageUrl,
                    lastFrameImageBase64: data.last_frame_image_url ? '' : prev.lastFrameImageBase64,
                    results: [...prev.results, nextResult],
                    previewIndex: prev.results.length,
                    save_dialog_show_state: false,
                }), resolve);
            });

            await this.loadHistoryList(this.state.auto_yn);
            await this.loadAiUsage();
            this.notifyStudioListRefresh({
                ai_video_studio_history_seq: data.ai_video_studio_history_seq,
                ai_video_studio_result_seq: data.ai_video_studio_result_seq,
            });
            // 생성 직후 status API를 한 번 즉시 호출해 서버의 result 전용 백그라운드 poller를 시작합니다.
            // 이후 프론트 polling은 DB에 반영된 DONE/FAIL 상태를 화면에 갱신하는 용도입니다.
            this.pollResult(data.ai_video_studio_result_seq, true);
        } catch (error) {
            console.error('AI 동영상 생성 요청 실패:', error);
            this.setState({ isGenerating: false, save_dialog_show_state: false });
            this.openWarning(error?.response?.data?.message || error?.message || 'AI 동영상을 생성하지 못했습니다.');
        }
    };

    pollResult = (resultSeq, immediate = false) => {
        if (!resultSeq) {
            this.setState({ isGenerating: false });
            return;
        }
        this.clearPollTimer();
        this.activePollResultSeq = resultSeq;

        if (immediate) {
            this.checkResultStatus(resultSeq);
            return;
        }

        this.pollTimer = window.setTimeout(() => this.checkResultStatus(resultSeq), POLL_INTERVAL_MS);
    };

    checkResultStatus = async (resultSeq) => {
        const nextPollCount = Number(this.state.pollCount || 0) + 1;
        if (nextPollCount > MAX_POLL_COUNT) {
            this.clearPollTimer();
            this.setState({ isGenerating: false });
            this.openWarning('영상 생성 시간이 길어지고 있습니다.<br/>창을 닫아도 서버에서 생성 상태를 계속 확인하며 완료되면 히스토리에 반영됩니다.');
            return;
        }

        this.setState({ pollCount: nextPollCount });

        try {
            const response = await http.get('/rest/shop/aiVideoStudio/result/status', {
                params: { ai_video_studio_result_seq: resultSeq },
            });
            // 히스토리 이동/팝업 종료 후 이전 status 응답이 늦게 도착한 경우 무시합니다.
            if (String(this.activePollResultSeq || '') !== String(resultSeq || '')) {
                return;
            }

            const data = response && response.data ? response.data : {};
            const resultInfo = data.result_info || {};
            const status = String(data.status || resultInfo.run_status || '').toUpperCase();

            this.replaceResult(resultSeq, resultInfo);

            if (status === 'DONE') {
                this.clearPollTimer();
                this.setState({ isGenerating: false });
                this.loadHistoryList(this.state.auto_yn);
                this.notifyStudioListRefresh({
                    ai_video_studio_history_seq: resultInfo.ai_video_studio_history_seq || this.state.ai_video_studio_history_seq,
                    ai_video_studio_result_seq: resultSeq,
                    video_url: resultInfo.generated_video_url || '',
                });
                return;
            }

            if (status === 'FAIL' || status === 'FAILED' || status === 'UPLOAD_FAILED') {
                this.clearPollTimer();
                this.setState({ isGenerating: false });
                this.openWarning(resultInfo.error_message || '영상 생성에 실패했습니다. 다른 내용으로 다시 시도해 주세요.');
                return;
            }

            this.pollResult(resultSeq);
        } catch (error) {
            console.error('AI 동영상 생성 상태 확인 실패:', error);
            if (String(this.activePollResultSeq || '') === String(resultSeq || '')) {
                this.pollResult(resultSeq);
            }
        }
    };

    replaceResult = (resultSeq, patch) => {
        if (!patch || !resultSeq) return;
        this.setState((prev) => ({
            results: (prev.results || []).map((item) => (
                String(item.ai_video_studio_result_seq) === String(resultSeq)
                    ? { ...item, ...patch }
                    : item
            )),
        }));
    };

    clearPollTimer = () => {
        if (this.pollTimer) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
        this.activePollResultSeq = null;
    };

    scheduleCurrentSave = () => {
        if (this.state.tutorialDemoMode) return;
        if (!this.state.ai_video_studio_history_seq) return;
        if (this.currentSaveTimer) clearTimeout(this.currentSaveTimer);
        this.currentSaveTimer = window.setTimeout(this.saveCurrent, 700);
    };

    saveCurrent = async () => {
        if (this.state.tutorialDemoMode) return null;
        this.currentSaveTimer = null;
        if (!this.state.ai_video_studio_history_seq) return null;

        const savePromise = (async () => {
            try {
                const response = await http.post('/rest/shop/aiVideoStudio/saveCurrent', {
                    ai_video_studio_history_seq: this.state.ai_video_studio_history_seq,
                    prompt_text: this.state.prompt || '',
                    source_image_url: this.state.sourceImageUrl || '',
                    source_image_base64: this.state.sourceImageBase64 || '',
                    source_image_mime_type: this.getSourceImageMimeType(),
                    reference_images: this.getReferenceImagesPayload(),
                    last_frame_image_url: this.state.lastFrameImageUrl || '',
                    last_frame_image_base64: this.state.lastFrameImageBase64 || '',
                    last_frame_image_mime_type: this.getLastFrameImageMimeType(),
                    video_style_cd: this.state.videoStyleCd || '',
                    camera_angle_cd: this.state.cameraAngleCd || '',
                    camera_movement_cd: this.state.cameraMovementCd || '',
                    aspect_ratio: this.state.aspectRatio || '9:16',
                    duration_seconds: Number(this.state.durationSeconds || 8),
                    resolution: this.state.resolution || '720p',
                });
                const data = response && response.data ? response.data : {};
                if (data.result === 'success') {
                    const patch = {
                        // 과거 RESULT를 열어 수정한 내용이 HISTORY.current_*에 저장되었으므로
                        // 이후 복제는 과거 RESULT가 아니라 현재 최종 상태를 기준으로 합니다.
                        duplicateSourceResultSeq: null,
                    };
                    if (data.source_image_url && this.state.sourceImageBase64) {
                        patch.sourceImageUrl = data.source_image_url;
                        patch.sourceImageBase64 = '';
                    }
                    if (Array.isArray(data.reference_images)) {
                        patch.referenceImages = (this.state.referenceImages || []).map((item, index) => {
                            const savedItem = data.reference_images[index];

                            if (!savedItem) {
                                return item;
                            }

                            return {
                                ...item,

                                // 서버에서 변환된 값만 반영
                                imageUrl: savedItem.image_url || item.imageUrl || '',
                                imageBase64: savedItem.image_url ? '' : item.imageBase64,
                                mimeType: savedItem.image_mime_type || item.mimeType || 'image/png',

                                // 아래 값은 현재 프론트 값을 그대로 유지
                                id: item.id,
                                fileName: item.fileName,
                                description: item.description,
                            };
                        });
                    }
                    if (data.last_frame_image_url && this.state.lastFrameImageBase64) {
                        patch.lastFrameImageUrl = data.last_frame_image_url;
                        patch.lastFrameImageBase64 = '';
                    }
                    await new Promise((resolve) => this.setState(patch, resolve));
                    return data;
                }
                return null;
            } catch (error) {
                console.error('AI 동영상 스튜디오 현재 입력 상태 저장 실패:', error);
                return null;
            }
        })();

        this.currentSavePromise = savePromise;
        try {
            return await savePromise;
        } finally {
            if (this.currentSavePromise === savePromise) {
                this.currentSavePromise = null;
            }
        }
    };

    loadHistoryList = (autoYn = this.state.auto_yn || 'N') => {
        return http.get('/rest/shop/aiVideoStudio/historyList', { params: { auto_yn: autoYn } })
            .then((res) => {
                const data = res && res.data ? res.data : {};
                const historyItems = data.result === 'success' && Array.isArray(data.history_list)
                    ? data.history_list
                    : [];
                this.setState({ historyItems });
                return historyItems;
            })
            .catch((error) => {
                console.error('AI 동영상 스튜디오 히스토리 목록 조회 실패:', error);
                return [];
            });
    };

    loadHistoryDetail = (historySeq, options = {}) => {
        if (!historySeq) return Promise.resolve();
        this.clearPollTimer();

        return http.get('/rest/shop/aiVideoStudio/historyDetail', {
            params: { ai_video_studio_history_seq: historySeq },
        }).then((res) => {
            const data = res && res.data ? res.data : {};
            if (data.result !== 'success') throw new Error(data.message || '히스토리를 불러올 수 없습니다.');

            const history = data.history_info || {};
            const results = Array.isArray(data.result_list) ? data.result_list : [];
            let previewIndex = this.findInitialPreviewIndex(results, history, options);
            if (previewIndex < 0) previewIndex = results.length > 0 ? results.length - 1 : 0;

            // 일반 히스토리 클릭은 master의 마지막 입력값을 사용합니다.
            // AI 스튜디오 목록에서 특정 결과를 열 때만 그 결과 생성 당시 설정으로 초기화합니다.
            let inputState = {
                prompt: history.current_prompt_text || '',
                sourceImageUrl: history.current_source_image_url || '',
                referenceImages: normalizeReferenceImages(history.current_reference_images || history.current_reference_images_json || []),
                lastFrameImageUrl: history.current_last_frame_image_url || '',
                videoStyleCd: history.current_video_style_cd || '',
                cameraAngleCd: history.current_camera_angle_cd || '',
                cameraMovementCd: history.current_camera_movement_cd || '',
                aspectRatio: normalizeAspectRatio(history.current_aspect_ratio || '9:16'),
                durationSeconds: Number(history.current_duration_seconds || 8),
                resolution: history.current_resolution || '720p',
            };

            if (options.useInitialResult && results[previewIndex]) {
                const selected = results[previewIndex];
                inputState = {
                    prompt: firstNonEmpty(this.props.initial_prompt, selected.prompt_text, inputState.prompt),
                    sourceImageUrl: firstNonEmpty(this.props.initial_source_image_url, selected.source_image_url, inputState.sourceImageUrl),
                    referenceImages: normalizeReferenceImages(firstNonEmpty(this.props.initial_reference_images, selected.reference_images, selected.reference_images_json, inputState.referenceImages)),
                    lastFrameImageUrl: firstNonEmpty(this.props.initial_last_frame_image_url, selected.last_frame_image_url, inputState.lastFrameImageUrl),
                    videoStyleCd: firstNonEmpty(this.props.initial_video_style_cd, selected.video_style_cd, inputState.videoStyleCd),
                    cameraAngleCd: firstNonEmpty(this.props.initial_camera_angle_cd, selected.camera_angle_cd, inputState.cameraAngleCd),
                    cameraMovementCd: firstNonEmpty(this.props.initial_camera_movement_cd, selected.camera_movement_cd, inputState.cameraMovementCd),
                    aspectRatio: normalizeAspectRatio(firstNonEmpty(this.props.initial_aspect_ratio, selected.aspect_ratio, inputState.aspectRatio)),
                    durationSeconds: Number(firstNonEmpty(this.props.initial_duration_seconds, selected.duration_seconds, inputState.durationSeconds) || 8),
                    resolution: firstNonEmpty(this.props.initial_resolution, selected.resolution, inputState.resolution) || '720p',
                };
            }

            const initialResult = results[previewIndex] || null;
            const isMatchedInitialResult = Boolean(
                options.useInitialResult
                && initialResult
                && (
                    (options.initialResultSeq && String(initialResult.ai_video_studio_result_seq || '') === String(options.initialResultSeq))
                    || (options.initialResultNo && String(initialResult.result_no || '') === String(options.initialResultNo))
                    || (this.props.initial_result_video_url && String(initialResult.generated_video_url || '') === String(this.props.initial_result_video_url))
                )
            );

            // 히스토리 복원은 Starter 원본/props를 절대 참조하지 않습니다.
            // 생성 당시 DB에 저장된 sample 여부 + prompt/meta snapshot만 사용합니다.
            const starterSampleYn = String(history.starter_sample_yn || 'N').toUpperCase() === 'Y';
            const starterMeta = starterSampleYn
                ? this.parseStarterMetaSnapshot(history.starter_meta_snapshot_json)
                : null;
            const starterUserReferenceConfig = starterSampleYn && starterMeta
                ? normalizeUserReferenceImageConfig(starterMeta.user_reference_image || starterMeta.userReferenceImage || null)
                : null;

            this.setState({
                ai_video_studio_history_seq: history.ai_video_studio_history_seq,
                selectedHistorySeq: history.ai_video_studio_history_seq,
                workflowTitle: history.history_title || '',
                duplicateSourceResultSeq: isMatchedInitialResult
                    ? (initialResult.ai_video_studio_result_seq || null)
                    : null,
                samplePrompt: starterSampleYn ? String(history.starter_prompt_snapshot || '').trim() : '',
                sampleUserReferenceConfig: starterUserReferenceConfig,
                sampleUserReferenceDescription: starterSampleYn && starterMeta
                    ? String((starterUserReferenceConfig && starterUserReferenceConfig.image_description) || starterMeta.user_reference_description || '').trim()
                    : '',
                sampleGuide: starterSampleYn ? {
                    title: (starterMeta && starterMeta.title) || history.history_title || '',
                    sceneDescription: (starterMeta && (starterMeta.scene_description || starterMeta.sceneDescription)) || '',
                    imageGuide: (starterUserReferenceConfig && starterUserReferenceConfig.image_guide)
                        || (starterMeta && (starterMeta.image_guide || starterMeta.imageGuide)) || '',
                } : null,
                sampleMetaSnapshot: starterSampleYn ? starterMeta : null,
                ...inputState,
                sourceImageBase64: '',
                sourceImageFileName: '',
                lastFrameImageBase64: '',
                lastFrameImageFileName: '',
                results,
                previewIndex,
                isGenerating: results.some((item) => String(item.run_status || '').toUpperCase() === 'PROCESSING'),
                pollCount: 0,
                isMobileHistoryOpen: false,
                imageSourceModalOpen: false,
                imageSourceTarget: '',
                referenceImagePopoverOpen: false,
                showAiImageStudioPopup: false,
                historyMenuSeq: null,
            }, () => {
                const processing = [...results].reverse().find((item) => String(item.run_status || '').toUpperCase() === 'PROCESSING');
                if (processing && processing.ai_video_studio_result_seq) {
                    this.pollResult(processing.ai_video_studio_result_seq);
                }
            });
        }).catch((error) => {
            console.error('AI 동영상 스튜디오 히스토리 상세 조회 실패:', error);
            this.openWarning(error?.response?.data?.message || error?.message || '히스토리를 불러올 수 없습니다.');
        });
    };

    findInitialPreviewIndex = (results, history, options) => {
        if (options && options.useInitialResult) {
            const initialSeq = options.initialResultSeq;
            const initialNo = options.initialResultNo;
            const index = (results || []).findIndex((item) => (
                (initialSeq && String(item.ai_video_studio_result_seq) === String(initialSeq)) ||
                (initialNo && String(item.result_no) === String(initialNo)) ||
                (this.props.initial_result_video_url && String(item.generated_video_url || '') === String(this.props.initial_result_video_url))
            ));
            if (index >= 0) return index;
        }

        if (history && history.selected_result_seq) {
            const selectedIndex = (results || []).findIndex((item) => (
                String(item.ai_video_studio_result_seq) === String(history.selected_result_seq)
            ));
            if (selectedIndex >= 0) return selectedIndex;
        }

        return (results || []).length - 1;
    };

    handleHistoryItemClick = (item) => {
        if (!item || this.state.historyActionProcessing) return;
        this.loadHistoryDetail(item.ai_video_studio_history_seq);
    };

    toggleHistoryMenu = (event, item) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        const seq = item && item.ai_video_studio_history_seq;
        this.setState((prev) => ({
            historyMenuSeq: String(prev.historyMenuSeq || '') === String(seq || '') ? null : seq,
        }));
    };

    handleHistoryRename = (event, item) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (!item) return;
        this.setState({ historyMenuSeq: null, historyRenameDialogOpen: true, historyRenameTarget: item });
    };

    closeHistoryRenameDialog = () => {
        if (this.state.historyActionProcessing) return;
        this.setState({ historyRenameDialogOpen: false, historyRenameTarget: null });
    };

    confirmHistoryRename = async (inputValues = []) => {
        const target = this.state.historyRenameTarget;
        const title = String(inputValues[0] || '').trim();
        if (!target) return this.closeHistoryRenameDialog();
        if (!title) return this.openWarning('변경할 히스토리 이름을 입력해 주세요.');

        this.setState({
            historyRenameDialogOpen: false,
            historyRenameTarget: null,
            historyActionProcessing: true,
            save_dialog_show_state: true,
            save_dialog_content: '이름 변경 중입니다.',
        });

        try {
            const response = await http.put('/rest/shop/aiVideoStudio/history/title', {
                ai_video_studio_history_seq: target.ai_video_studio_history_seq,
                history_title: title,
            });
            const data = response && response.data ? response.data : {};
            if (data.result !== 'success') throw new Error(data.message || '이름 변경에 실패했습니다.');
            await this.loadHistoryList(this.state.auto_yn);
            this.setState((prev) => ({
                workflowTitle: String(prev.selectedHistorySeq || '') === String(target.ai_video_studio_history_seq)
                    ? title
                    : prev.workflowTitle,
                historyActionProcessing: false,
                save_dialog_show_state: false,
            }));
        } catch (error) {
            this.setState({ historyActionProcessing: false, save_dialog_show_state: false });
            this.openWarning(error?.response?.data?.message || error?.message || '히스토리 이름을 변경하지 못했습니다.');
        }
    };

    handleHistoryDuplicate = async (event, item) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        const seq = item && item.ai_video_studio_history_seq;
        if (!seq || this.state.historyActionProcessing) return;

        this.setState({
            historyMenuSeq: null,
            historyActionProcessing: true,
            save_dialog_show_state: true,
            save_dialog_content: '새 작업으로 복제 중입니다.',
            save_dialog_button_list: [{ title: '확인', type: '1', display_yn: 'N' }],
        });

        try {
            const isCurrentDisplayedHistory =
                String(this.state.ai_video_studio_history_seq || '') === String(seq || '');

            // 과거 RESULT를 연 뒤 수정 직후 자동 저장이 아직 대기 중이면 먼저 저장합니다.
            // 저장에 성공하면 duplicateSourceResultSeq가 해제되어 HISTORY.current_* 기준으로 복제됩니다.
            if (isCurrentDisplayedHistory && this.currentSaveTimer) {
                clearTimeout(this.currentSaveTimer);
                this.currentSaveTimer = null;
                const saveResult = await this.saveCurrent();
                if (!saveResult) {
                    throw new Error('현재 수정 내용을 저장하지 못해 새 작업으로 복제할 수 없습니다.');
                }
            } else if (isCurrentDisplayedHistory && this.currentSavePromise) {
                const saveResult = await this.currentSavePromise;
                if (!saveResult || saveResult.result !== 'success') {
                    throw new Error('현재 수정 내용을 저장하지 못해 새 작업으로 복제할 수 없습니다.');
                }
            }

            const duplicateSourceResultSeq = isCurrentDisplayedHistory
                ? this.state.duplicateSourceResultSeq
                : null;
            const duplicateRequestConfig = duplicateSourceResultSeq
                ? { params: { source_result_seq: duplicateSourceResultSeq } }
                : undefined;

            const response = await http.post(
                `/rest/shop/aiVideoStudio/history/${encodeURIComponent(seq)}/duplicate`,
                null,
                duplicateRequestConfig
            );
            const data = response && response.data ? response.data : {};
            if (data.result !== 'success' && data.status !== 'success') {
                throw new Error(data.message || '새 작업 복제에 실패했습니다.');
            }

            await this.loadHistoryList(this.state.auto_yn);
            await this.loadHistoryDetail(data.ai_video_studio_history_seq);
            this.setState({
                historyActionProcessing: false,
                save_dialog_show_state: true,
                save_dialog_content: '새 작업으로 복제되었습니다.',
                save_dialog_button_list: [{
                    title: '확인',
                    type: '1',
                    display_yn: 'Y',
                    event_method: () => this.setState({ save_dialog_show_state: false }),
                }],
            });
        } catch (error) {
            this.setState({ historyActionProcessing: false, save_dialog_show_state: false });
            this.openWarning(error?.response?.data?.message || error?.message || '새 작업으로 복제하지 못했습니다.');
        }
    };

    handleHistoryDelete = (event, item) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (!item) return;
        this.setState({ historyMenuSeq: null, historyDeleteDialogOpen: true, historyDeleteTarget: item });
    };

    closeHistoryDeleteDialog = () => {
        if (this.state.historyActionProcessing) return;
        this.setState({ historyDeleteDialogOpen: false, historyDeleteTarget: null });
    };

    deleteHistory = async () => {
        const target = this.state.historyDeleteTarget;
        if (!target || this.state.historyActionProcessing) return;
        const seq = target.ai_video_studio_history_seq;
        const deletingCurrent = String(seq) === String(this.state.selectedHistorySeq || '');

        this.setState({
            historyDeleteDialogOpen: false,
            historyActionProcessing: true,
            save_dialog_show_state: true,
            save_dialog_content: '삭제 중입니다.',
        });

        try {
            const response = await http.delete(`/rest/shop/aiVideoStudio/history/${encodeURIComponent(seq)}`);
            const data = response && response.data ? response.data : {};
            if (String(data.result || '').toLowerCase() === 'used') {
                this.setState({ historyActionProcessing: false, historyDeleteTarget: null, save_dialog_show_state: false });
                this.openWarning('영상 자동화 설정에 사용 중인 히스토리는 삭제할 수 없습니다.<br/>자동화 설정을 변경한 후 다시 시도해 주세요.');
                return;
            }
            if (data.result !== 'success') throw new Error(data.message || '삭제에 실패했습니다.');

            const remaining = await this.loadHistoryList(this.state.auto_yn);
            if (deletingCurrent) {
                if (remaining.length) await this.loadHistoryDetail(remaining[0].ai_video_studio_history_seq);
                else this.startNewWorkflow();
            }
            this.setState({ historyActionProcessing: false, historyDeleteTarget: null, save_dialog_show_state: false });
        } catch (error) {
            this.setState({ historyActionProcessing: false, historyDeleteTarget: null, save_dialog_show_state: false });
            this.openWarning(error?.response?.data?.message || error?.message || '히스토리를 삭제하지 못했습니다.');
        }
    };

    movePreview = (direction) => {
        const length = this.state.results.length;
        if (!length) return;
        const next = Math.max(0, Math.min(length - 1, this.state.previewIndex + direction));
        // 이미지 AI 스튜디오와 동일하게 결과만 넘기고, 현재 입력 중인 프롬프트/설정은 변경하지 않습니다.
        this.setState({ previewIndex: next });
    };

    getPreviewResult = () => this.state.results[this.state.previewIndex] || null;

    getCurrentVideoDownloadFileName = (result, videoUrl) => {
        const seq = result && (result.ai_video_studio_result_seq || result.aiVideoStudioResultSeq || result.result_seq || result.seq || '');
        const urlWithoutQuery = String(videoUrl || '').split('?')[0].split('#')[0];
        const extMatch = urlWithoutQuery.match(/\.([a-zA-Z0-9]+)$/);
        const ext = extMatch ? extMatch[1] : 'mp4';

        return `robomd_ai_video_studio${seq ? `_${seq}` : ''}.${ext}`;
    };

    downloadCurrentVideo = async (event) => {
        if (this.state.tutorialDemoMode) {
            if (event) event.preventDefault();
            return;
        }
        if (event) event.stopPropagation();

        const result = this.getPreviewResult();
        const videoUrl = result && result.generated_video_url;
        if (!videoUrl) return;

        const downloadFileName = this.getCurrentVideoDownloadFileName(result, videoUrl);

        try {
            const cacheBuster = videoUrl.includes('?') ? `&t=${new Date().getTime()}` : `?t=${new Date().getTime()}`;
            const response = await fetch(videoUrl + cacheBuster);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = downloadFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('영상 다운로드 실패', error);

            const link = document.createElement('a');
            link.href = videoUrl;
            link.download = downloadFileName;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    applyToMain = async (applyType) => {
        if (this.state.tutorialDemoMode) return;
        const requestedType = applyType || this.getAiResponseType();

        if (requestedType === 'PROMPT') {
            const prompt = String(this.state.prompt || '').trim();
            const executablePrompt = String(this.state.samplePrompt || '').trim() || prompt;
            if (!executablePrompt) return this.openWarning('자동화 규칙으로 적용할 영상 생성 내용을 입력해 주세요.');

            try {
                const response = await http.post('/rest/shop/aiVideoStudio/saveRule', this.buildRequestBody());
                const data = response && response.data ? response.data : {};
                if (data.result !== 'success') throw new Error(data.message || '영상 생성 규칙을 저장할 수 없습니다.');

                this.setState({
                    ai_video_studio_history_seq: data.ai_video_studio_history_seq,
                    selectedHistorySeq: data.ai_video_studio_history_seq,
                    sourceImageUrl: data.source_image_url || this.state.sourceImageUrl,
                    sourceImageBase64: data.source_image_url ? '' : this.state.sourceImageBase64,
                    referenceImages: data.reference_images ? normalizeReferenceImages(data.reference_images) : this.state.referenceImages,
                    lastFrameImageUrl: data.last_frame_image_url || this.state.lastFrameImageUrl,
                    lastFrameImageBase64: data.last_frame_image_url ? '' : this.state.lastFrameImageBase64,
                });

                if (typeof this.props.onApplyRule === 'function') {
                    this.props.onApplyRule(this.buildApplyPayload('PROMPT', null, data));
                }
                this.closeModal();
            } catch (error) {
                this.openWarning(error?.response?.data?.message || error?.message || '영상 자동화 규칙을 적용하지 못했습니다.');
            }
            return;
        }

        const selected = this.getPreviewResult();
        if (!selected || String(selected.run_status || '').toUpperCase() !== 'DONE' || !selected.generated_video_url) {
            this.openWarning('적용할 완료된 영상을 선택해 주세요.');
            return;
        }

        try {
            const response = await http.post('/rest/shop/aiVideoStudio/selectResult', {
                ai_video_studio_history_seq: selected.ai_video_studio_history_seq || this.state.ai_video_studio_history_seq,
                ai_video_studio_result_seq: selected.ai_video_studio_result_seq,
            });
            const data = response && response.data ? response.data : {};
            if (data.result !== 'success') throw new Error(data.message || '영상을 선택할 수 없습니다.');

            if (typeof this.props.onApplyRule === 'function') {
                this.props.onApplyRule(this.buildApplyPayload('VIDEO', selected, data));
            }
            this.closeModal();
        } catch (error) {
            this.openWarning(error?.response?.data?.message || error?.message || '선택한 영상을 적용하지 못했습니다.');
        }
    };

    buildApplyPayload = (type, selectedResult, savedData = {}) => {
        const historySeq = savedData.ai_video_studio_history_seq || this.state.ai_video_studio_history_seq || null;
        const selected = selectedResult || {};
        const rule = {
            prompt: this.state.prompt || '',
            starter_sample_yn: this.state.samplePrompt ? 'Y' : 'N',
            starter_prompt_snapshot: this.state.samplePrompt || '',
            starter_meta_snapshot_json: this.state.samplePrompt ? JSON.stringify(this.buildStarterMetaSnapshot()) : '',
            source_image_url: savedData.source_image_url || this.state.sourceImageUrl || '',
            reference_images: savedData.reference_images || this.getReferenceImagesPayload(),
            last_frame_image_url: savedData.last_frame_image_url || this.state.lastFrameImageUrl || '',
            video_style_cd: this.state.videoStyleCd || '',
            camera_angle_cd: this.state.cameraAngleCd || '',
            camera_movement_cd: this.state.cameraMovementCd || '',
            aspect_ratio: this.state.aspectRatio || '9:16',
            duration_seconds: Number(this.state.durationSeconds || 8),
            resolution: this.state.resolution || '720p',
        };

        return {
            ai_response_type: type,
            appliedValue: type === 'VIDEO' ? selected.generated_video_url || '' : rule,
            appliedVideo: type === 'VIDEO' ? selected.generated_video_url || '' : '',
            appliedPrompt: rule.prompt,
            appliedPrompts: [{
                stepNumber: 1,
                prompt: rule.prompt,
                modeText: rule.reference_images && rule.reference_images.length ? '(참조 이미지 기반 영상 생성)' : (rule.source_image_url || this.state.sourceImageBase64 ? '(이미지 기반 영상 생성)' : '(텍스트 기반 영상 생성)'),
            }],
            appliedThumb: rule.source_image_url || ((rule.reference_images && rule.reference_images[0] && rule.reference_images[0].image_url) || ''),
            video_rule: rule,
            ai_video_studio_history_seq: historySeq,
            ai_video_studio_result_seq: selected.ai_video_studio_result_seq || '',
            result_no: selected.result_no || '',
            history_title: savedData.history_title || this.state.workflowTitle || (this.state.sampleGuide && this.state.sampleGuide.title) || this.makeHistoryTitle(rule.prompt),
            aspect_ratio: rule.aspect_ratio,
            ratio: rule.aspect_ratio.replace(':', '/'),
            duration_seconds: rule.duration_seconds,
            resolution: rule.resolution,
        };
    };

    notifyStudioListRefresh = (payload = {}) => {
        if (!this.isOpenedFromAiStudioList()) return;
        if (typeof this.props.onRefreshStudioVideoList === 'function') {
            this.props.onRefreshStudioVideoList({ reset: true, start: 0, ...payload });
        }
    };

    makeHistoryTitle = (prompt) => {
        const normalized = String(prompt || '').replace(/\s+/g, ' ').trim();
        if (!normalized) return '새 영상 생성';
        return normalized.length > 32 ? `${normalized.substring(0, 32)}...` : normalized;
    };

    groupHistoryByDate = (items) => {
        const grouped = {};
        const today = new Date();
        (items || []).forEach((item) => {
            const raw = item.mod_date || item.reg_date;
            const normalized = raw ? String(raw).replace(/([+-]\d{2})(\d{2})$/, '$1:$2') : '';
            const date = normalized ? new Date(normalized) : null;
            let key = '날짜없음';
            if (date && !Number.isNaN(date.getTime())) {
                const isToday = date.getFullYear() === today.getFullYear()
                    && date.getMonth() === today.getMonth()
                    && date.getDate() === today.getDate();
                key = isToday
                    ? '오늘'
                    : `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
            }
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(item);
        });
        return grouped;
    };

    renderPills = (options, selected, onSelect, disabledFn, showTooltip = false) => (
        <div className="pill-option-group">
            {options.map((option) => {
                const value = typeof option === 'object' ? option.value : option;
                const label = typeof option === 'object' ? option.label : option;
                const description = typeof option === 'object' ? option.desc || '' : '';
                const disabled = typeof disabledFn === 'function' ? disabledFn(value) : false;
                return (
                    <div
                        className="pill-option-item"
                        key={String(value)}
                        onMouseEnter={this.adjustImageControlTooltipPosition}
                        onFocusCapture={this.adjustImageControlTooltipPosition}
                    >
                        <button
                            type="button"
                            className={`pill-option-btn ${String(selected) === String(value) ? 'active' : ''}`}
                            disabled={disabled}
                            onClick={() => !disabled && onSelect(value)}
                        >
                            {label}
                        </button>
                        {showTooltip && value !== '' && description ? (
                            <div className="pill-option-tooltip">{description}</div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );

    renderReferenceImagePopover = (referenceImages = [], hasReferenceImages = false, referenceDisabled = false) => {
        if (!this.state.referenceImagePopoverOpen || referenceDisabled || typeof document === 'undefined') return null;

        const position = this.state.referenceImagePopoverPosition || {};
        const starterReferenceSample = this.isStarterReferenceSample();
        const starterUserReferenceConfig = starterReferenceSample ? this.getStarterUserReferenceConfig() : null;
        const fixedReferenceCount = starterReferenceSample ? this.getStarterFixedReferenceCount() : 0;
        // Starter 샘플의 기본 참조 이미지는 생성 데이터에 유지하되 팝업에서는 사용자 입력 슬롯만 노출합니다.
        const visibleReferenceIndexes = starterReferenceSample
            ? Array.from(
                { length: Math.max(0, this.getReferenceImageMaxCount() - fixedReferenceCount) },
                (_, index) => fixedReferenceCount + index
            )
            : Array.from({ length: MAX_REFERENCE_IMAGE_COUNT }, (_, index) => index);
        const slotCount = visibleReferenceIndexes.length;
        // 참조 이미지 입력 슬롯이 답답하지 않도록 슬롯 수에 따라 팝업 폭을 조금 더 여유 있게 확장합니다.
        const referencePopoverWidth = 245 + (Math.max(1, slotCount) - 1) * 205;
        const popoverStyle = {
            top: `${Number(position.top) || 0}px`,
            left: `${Number(position.left) || 0}px`,
            '--reference-popover-arrow-left': `${Number(position.arrowLeft) || 32}px`,
            '--reference-popover-width': `${referencePopoverWidth}px`,
        };
        const starterUi = starterReferenceSample ? this.getStarterReferenceUi() : null;

        return ReactDOM.createPortal(
            <div className="ai-video-studio-workflow reference-image-popover-portal-root">
                <div
                    ref={this.referenceImagePopoverRef}
                    className={`reference-image-popover placement-${position.placement || 'bottom'} ${position.ready ? '' : 'is-positioning'}`}
                    style={popoverStyle}
                    role="dialog"
                    aria-modal="false"
                    aria-labelledby="referenceImagePopoverTitle"
                >
                    <div className="reference-image-popover-scroll">
                    <div className="reference-popover-header">
                        <div id="referenceImagePopoverTitle" className="reference-popover-title">
                            {starterReferenceSample ? '참조 이미지' : `참조 이미지 ${referenceImages.length}/${MAX_REFERENCE_IMAGE_COUNT}`}
                        </div>
                        <button type="button" className="reference-popover-close" onClick={this.closeReferenceImagePopover} aria-label="참조 이미지 관리 닫기">
                            <i className="fa fa-times" aria-hidden="true" />
                        </button>
                    </div>
                    {!starterReferenceSample ? (
                        <div className="reference-popover-desc">메인 프롬프트에서 각 참조 이미지가 무엇인지 구분할 수 있도록 각 참조 이미지의 제목을 입력해주세요.</div>
                    ) : null}
                    <div
                        className={`reference-popover-grid ${starterReferenceSample ? 'sample-reference-grid' : ''}`}
                        style={starterReferenceSample ? { gridTemplateColumns: `repeat(${Math.max(slotCount, 1)}, minmax(0,1fr))` } : undefined}
                    >
                        {visibleReferenceIndexes.map((index, visibleIndex) => {
                            const item = referenceImages[index];
                            const preview = item ? (item.imageBase64 || item.imageUrl || '') : '';
                            const fixedSampleReference = starterReferenceSample && index < fixedReferenceCount;
                            const starterUserReference = starterReferenceSample
                                && index >= fixedReferenceCount
                                && index < fixedReferenceCount + Number((starterUserReferenceConfig && starterUserReferenceConfig.max_count) || 1);
                            const starterInputValues = starterUserReference && item
                                ? getReferenceInputValues(starterUserReferenceConfig, item)
                                : {};
                            return (
                                <div className="reference-popover-item" key={item ? (item.id || index) : `empty-reference-${index}`}>
                                    {item ? (
                                        <>
                                            <div className={`reference-slot filled ${fixedSampleReference ? 'sample-fixed-reference' : ''}`} style={{ backgroundImage: preview ? `url(${preview})` : undefined }}>
                                                {!fixedSampleReference ? (
                                                    <button type="button" className="reference-slot-remove" onClick={() => this.removeReferenceImage(index)} aria-label={`${visibleIndex + 1}번 참조 이미지 삭제`}>
                                                        <i className="fa fa-times" aria-hidden="true" />
                                                    </button>
                                                ) : null}
                                            </div>
                                            <div className="reference-slot-name">
                                                {fixedSampleReference
                                                    ? (item.fileName || '샘플 참조 이미지')
                                                    : (starterUserReference ? starterUi.infoLabel : '참조 이미지 제목')}
                                            </div>
                                            {!fixedSampleReference ? (
                                                starterUserReference ? (
                                                    (starterUi.inputFields || []).length ? (
                                                        <div className="reference-user-fields">
                                                            {(starterUi.inputFields || []).map((field) => (
                                                                <label className="reference-user-field" key={field.key}>
                                                                    <span className="reference-user-field-label">{field.label}:</span>
                                                                    {(field.type === 'textarea' || String(field.label || '').trim() === '제품 특징') ? (
                                                                        <textarea
                                                                            className="reference-slot-description"
                                                                            value={starterInputValues[field.key] || ''}
                                                                            maxLength={field.max_length || MAX_REFERENCE_DESCRIPTION_LENGTH}
                                                                            placeholder={field.placeholder || ''}
                                                                            onChange={(event) => this.changeStarterReferenceField(index, field, event.target.value)}
                                                                            style={{ resize: "none" }}
                                                                        />
                                                                    ) : (
                                                                        <input
                                                                            type="text"
                                                                            className="reference-user-input"
                                                                            value={starterInputValues[field.key] || ''}
                                                                            maxLength={field.max_length || MAX_REFERENCE_DESCRIPTION_LENGTH}
                                                                            placeholder={field.placeholder || ''}
                                                                            onChange={(event) => this.changeStarterReferenceField(index, field, event.target.value)}
                                                                        />
                                                                    )}
                                                                </label>
                                                            ))}
                                                        </div>
                                                    ) : null
                                                ) : (
                                                    <textarea
                                                        className="reference-slot-description"
                                                        value={item.description || ''}
                                                        maxLength={MAX_REFERENCE_DESCRIPTION_LENGTH}
                                                        placeholder="예: 여성 모델, 분홍색 원피스"
                                                        onChange={(event) => this.changeReferenceImageDescription(index, event.target.value)}
                                                        style={{ resize: "none" }}
                                                    />
                                                )
                                            ) : null}
                                        </>
                                    ) : (
                                        <button type="button" className="reference-slot slot-add" onClick={() => this.openImageSourceModal('REFERENCE')}>
                                            <span className="reference-slot-add-inner">
                                                {this.renderImageAddIcon()}
                                                <span className="reference-slot-add-label">{starterUserReference ? starterUi.addLabel : '이미지 추가'}</span>
                                            </span>
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {hasReferenceImages && !starterReferenceSample ? (
                        <div className="reference-popover-note">참조 이미지를 사용하면 영상 길이는 8초로 고정되며 시작 이미지와 함께 사용할 수 없습니다.</div>
                    ) : null}
                    </div>

                </div>
            </div>,
            document.body
        );
    };

    adjustImageControlTooltipPosition = (event) => {
        const wrapper = event && event.currentTarget;
        if (!wrapper || typeof wrapper.querySelector !== 'function' || typeof window === 'undefined') {
            return;
        }

        const tooltip = wrapper.querySelector(':scope > .pill-option-tooltip');
        if (!tooltip) {
            return;
        }

        // 매번 기본 중앙·하단 배치로 되돌린 뒤 현재 화면 기준으로 다시 계산합니다.
        tooltip.style.removeProperty('--image-tooltip-left');
        tooltip.style.removeProperty('--image-tooltip-translate-x');
        tooltip.style.removeProperty('--image-tooltip-arrow-left');
        tooltip.classList.remove('placement-top');

        const boundary = wrapper.closest('.config-pane');
        const wrapperRect = wrapper.getBoundingClientRect();
        const boundaryRect = boundary
            ? boundary.getBoundingClientRect()
            : { left: 0, right: window.innerWidth, top: 0, bottom: window.innerHeight, width: window.innerWidth, height: window.innerHeight };
        const tooltipWidth = tooltip.offsetWidth;
        const tooltipHeight = tooltip.offsetHeight;

        if (!tooltipWidth || !tooltipHeight || !wrapperRect.width) {
            return;
        }

        const viewportPadding = 8;
        const boundaryPadding = 10;
        const arrowPadding = 12;
        const tooltipGap = 7;

        // 브라우저 화면과 config-pane이 겹치는 실제 표시 범위 안에서만 배치합니다.
        const visibleLeft = Math.max(viewportPadding, boundaryRect.left + boundaryPadding);
        const visibleRight = Math.min(window.innerWidth - viewportPadding, boundaryRect.right - boundaryPadding);
        const visibleTop = Math.max(viewportPadding, boundaryRect.top + boundaryPadding);
        const visibleBottom = Math.min(window.innerHeight - viewportPadding, boundaryRect.bottom - boundaryPadding);

        const triggerCenterX = wrapperRect.left + (wrapperRect.width / 2);
        const centeredLeft = triggerCenterX - (tooltipWidth / 2);
        const minLeft = visibleLeft;
        const maxLeft = visibleRight - tooltipWidth;
        const correctedLeft = maxLeft < minLeft
            ? minLeft
            : Math.min(Math.max(centeredLeft, minLeft), maxLeft);

        if (Math.abs(correctedLeft - centeredLeft) >= 0.5) {
            const relativeLeft = correctedLeft - wrapperRect.left;
            const arrowLeft = Math.min(
                Math.max(triggerCenterX - correctedLeft, arrowPadding),
                Math.max(tooltipWidth - arrowPadding, arrowPadding)
            );

            tooltip.style.setProperty('--image-tooltip-left', `${relativeLeft}px`);
            tooltip.style.setProperty('--image-tooltip-translate-x', '0px');
            tooltip.style.setProperty('--image-tooltip-arrow-left', `${arrowLeft}px`);
        }

        const availableBelow = visibleBottom - wrapperRect.bottom;
        const availableAbove = wrapperRect.top - visibleTop;
        const requiredVerticalSpace = tooltipHeight + tooltipGap;

        // 아래쪽 공간이 부족하고 위쪽이 더 넓으면 버튼 위로 전환합니다.
        if (availableBelow < requiredVerticalSpace && availableAbove > availableBelow) {
            tooltip.classList.add('placement-top');
        }
    };

    renderImageControls = () => {
        const sourcePreview = this.getSourceImagePreview();
        const referenceImages = this.state.referenceImages || [];
        const hasReferenceImages = referenceImages.length > 0;
        const hasLastFrameImage = !!this.getLastFrameImagePreview();
        const sourceDisabled = hasReferenceImages;
        const referenceDisabled = !!sourcePreview || hasLastFrameImage;
        const starterReferenceSample = this.isStarterReferenceSample();
        const referenceTooltip = hasLastFrameImage && !sourcePreview
            ? '참조 이미지는 기존 마지막 이미지와 함께 사용할 수 없습니다.'
            : '참조 이미지는 시작 이미지와 함께 사용할 수 없습니다.';

        return (
            <div className="compact-image-section" data-instagram-tutorial-target="VIDEO_STUDIO_IMAGES">
                <div className={`compact-image-guide ${starterReferenceSample ? 'sample-reference-guide' : ''}`}>
                    <i className="fa fa-star" aria-hidden="true" />
                    <span>{this.state.sampleGuide && this.state.sampleGuide.imageGuide
                        ? this.state.sampleGuide.imageGuide
                        : '이미지를 추가하면 원하는 인물·제품·장면을 더 정확하게 표현할 수 있어요.'}</span>
                </div>
                <div className="compact-image-controls">
                    <div
                        className="image-control-item"
                        data-instagram-tutorial-target="VIDEO_STUDIO_START_IMAGE"
                        onMouseEnter={this.adjustImageControlTooltipPosition}
                        onFocusCapture={this.adjustImageControlTooltipPosition}
                    >
                        <button
                            type="button"
                            className={`compact-image-btn ${sourcePreview ? 'active has-remove' : ''}`}
                            aria-disabled={sourceDisabled ? 'true' : 'false'}
                            onClick={() => !sourceDisabled && this.openImageSourceModal('SOURCE')}
                        >
                            {sourcePreview ? (
                                <span className="compact-image-thumb" style={{ backgroundImage: `url(${sourcePreview})` }} />
                            ) : (
                                <i className="fa fa-picture-o" aria-hidden="true" />
                            )}
                            <span>{sourcePreview ? '시작 이미지 적용 중' : '시작 이미지'}</span>
                        </button>
                        {sourcePreview && !sourceDisabled ? (
                            <button type="button" className="compact-image-remove" onClick={(event) => { event.stopPropagation(); this.removeSourceImage(); }} aria-label="시작 이미지 삭제">
                                <i className="fa fa-times" aria-hidden="true" />
                            </button>
                        ) : null}
                        <div className="pill-option-tooltip">
                            {sourceDisabled
                                ? '시작 이미지는 참조 이미지와 함께 사용할 수 없습니다.'
                                : '영상이 시작되는 첫 장면으로 사용할 이미지를 설정합니다.'}
                        </div>
                    </div>

                    <div
                        className="image-control-item"
                        data-instagram-tutorial-target="VIDEO_STUDIO_REFERENCE_IMAGES"
                        onMouseEnter={this.adjustImageControlTooltipPosition}
                        onFocusCapture={this.adjustImageControlTooltipPosition}
                    >
                        <button
                            ref={this.referenceImageTriggerRef}
                            type="button"
                            className={`compact-image-btn ${hasReferenceImages ? 'active' : ''}`}
                            aria-disabled={referenceDisabled ? 'true' : 'false'}
                            aria-expanded={this.state.referenceImagePopoverOpen ? 'true' : 'false'}
                            onClick={() => !referenceDisabled && this.toggleReferenceImagePopover()}
                        >
                            <i className="fa fa-clone" aria-hidden="true" />
                            <span>{starterReferenceSample
                                ? '참조 이미지'
                                : (hasReferenceImages ? `참조 이미지 ${referenceImages.length}/${MAX_REFERENCE_IMAGE_COUNT}` : '참조 이미지')}</span>
                            {starterReferenceSample
                                ? <i className={`fa fa-angle-${this.state.referenceImagePopoverOpen ? 'up' : 'down'}`} aria-hidden="true" />
                                : (!hasReferenceImages ? <span className="compact-image-limit">최대 3장</span> : <i className={`fa fa-angle-${this.state.referenceImagePopoverOpen ? 'up' : 'down'}`} aria-hidden="true" />)}
                        </button>
                        <div className="pill-option-tooltip">
                            {referenceDisabled
                                ? referenceTooltip
                                : '영상 속 인물·제품·공간 등의 모습을 참고할 이미지를 추가합니다.'}
                        </div>

                        {this.renderReferenceImagePopover(referenceImages, hasReferenceImages, referenceDisabled)}
                    </div>
                </div>

                {sourcePreview && this.isSourceImageAspectRatioMismatch() ? (
                    <div className="compact-image-warning">시작 이미지와 영상 비율이 다르면 여백이 생기거나 구도가 달라질 수 있습니다.</div>
                ) : null}

                <input ref={this.fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={this.handleSourceImageChange} />
                <input ref={this.referenceImagesInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={this.handleReferenceImagesChange} />
            </div>
        );
    };

    renderImageSourceModal = () => {
        if (!this.state.imageSourceModalOpen) return null;
        return (
            <div
                className="image-source-modal-overlay"
                onClick={(event) => {
                    if (event.target === event.currentTarget) this.closeImageSourceModal();
                }}
            >
                <div className="image-source-modal-box">
                    <button type="button" className="image-source-modal-close" onClick={this.closeImageSourceModal} aria-label="닫기">
                        <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16.0704 15.1788C16.1878 15.2973 16.2536 15.4573 16.2536 15.6241C16.2536 15.7909 16.1878 15.9509 16.0704 16.0694C15.9509 16.1849 15.7913 16.2495 15.6251 16.2495C15.4589 16.2495 15.2992 16.1849 15.1798 16.0694L10.0001 10.8819L4.82041 16.0694C4.70095 16.1849 4.54127 16.2495 4.3751 16.2495C4.20892 16.2495 4.04925 16.1849 3.92979 16.0694C3.81242 15.9509 3.74658 15.7909 3.74658 15.6241C3.74658 15.4573 3.81242 15.2973 3.92979 15.1788L9.11728 9.99912L3.92979 4.81943C3.83012 4.698 3.77919 4.54383 3.78689 4.38693C3.7946 4.23002 3.8604 4.08159 3.97148 3.9705C4.08256 3.85942 4.231 3.79362 4.3879 3.78592C4.54481 3.77821 4.69898 3.82914 4.82041 3.92881L10.0001 9.11631L15.1798 3.92881C15.3012 3.82914 15.4554 3.77821 15.6123 3.78592C15.7692 3.79362 15.9176 3.85942 16.0287 3.9705C16.1398 4.08159 16.2056 4.23002 16.2133 4.38693C16.221 4.54383 16.1701 4.698 16.0704 4.81943L10.8829 9.99912L16.0704 15.1788Z" fill="#333333" />
                        </svg>
                    </button>
                    <h3 className="image-source-modal-title">이미지 제작방식 선택</h3>
                    <div className="image-source-options">
                        <div className="image-source-option" onClick={() => this.handleImageSourceMethod('UPLOAD')}>
                            <div className="image-source-option-icon">{this.renderFolderIcon()}</div>
                            <h4 className="image-source-option-title">직접 올리기</h4>
                            <p className="image-source-option-desc">내 컴퓨터에서 선택</p>
                        </div>
                        <div className="image-source-option" onClick={() => this.handleImageSourceMethod('STUDIO')}>
                            <div className="image-source-option-icon">{this.renderImageStudioIcon()}</div>
                            <h4 className="image-source-option-title">이미지 스튜디오</h4>
                            <p className="image-source-option-desc">AI로 이미지 만들기</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    renderConfigPane = () => {
        const highResolution = this.state.resolution !== '720p';
        const hasLastFrameImage = !!this.getLastFrameImagePreview();
        const hasReferenceImages = this.hasReferenceImages();
        return (
            <div className="config-pane">
                <div className="config-header">
                    <div className="config-title">AI 동영상 만들기</div>
                    <div className="config-subtitle">원하는 장면과 움직임을 입력하고 필요한 항목만 선택해 주세요.</div>
                </div>

                <div className="config-body-scroll">
                    {this.state.sampleGuide && this.state.sampleGuide.sceneDescription ? (
                        <div className="sample-scene-guide">
                            {this.state.sampleGuide.title ? (
                                <div className="sample-scene-guide-title">{this.state.sampleGuide.title}</div>
                            ) : null}
                            <div className="sample-scene-guide-text">{this.state.sampleGuide.sceneDescription}</div>
                        </div>
                    ) : null}
                    <div className="prompt-flex-box" data-instagram-tutorial-target="VIDEO_STUDIO_PROMPT_AREA">
                        <div className="prompt-input-area">
                            <textarea
                                className="prompt-textarea"
                                data-instagram-tutorial-target="VIDEO_STUDIO_PROMPT"
                                value={this.state.prompt}
                                placeholder={this.state.sampleGuide?.promptPlaceholder || "예: 촉촉하고 쫀쫀한 수분크림 광고 영상으로 만들어줘. 물방울이 부드럽게 떠다니고 제품 쪽으로 천천히 줌인해줘."}
                                onChange={this.handlePromptChange}
                                // maxLength={PROMPT_MAX_LENGTH}
                            />
                            {/* <div className="prompt-counter">{String(this.state.prompt || '').length} / {PROMPT_MAX_LENGTH}</div> */}
                            {this.renderImageControls()}
                        </div>
                    </div>


                    <div className="settings-wrap">
                    <div data-instagram-tutorial-target="VIDEO_STUDIO_ASPECT_RATIO">
                        <span className="setting-section-title">영상 비율</span>
                        {this.renderPills(ASPECT_RATIO_OPTIONS, this.state.aspectRatio, (value) => this.setOption('aspectRatio', value))}
                    </div>
                    <div data-instagram-tutorial-target="VIDEO_STUDIO_DURATION">
                        <span className="setting-section-title">영상 길이</span>
                        {this.renderPills(DURATION_OPTIONS, this.state.durationSeconds, (value) => this.setOption('durationSeconds', value), (value) => (highResolution || hasLastFrameImage || hasReferenceImages) && Number(value) !== 8)}
                    </div>
                    <div data-instagram-tutorial-target="VIDEO_STUDIO_RESOLUTION">
                        <span className="setting-section-title">화질</span>
                        {this.renderPills(RESOLUTION_OPTIONS, this.state.resolution, (value) => this.setOption('resolution', value))}
                    </div>    
                    <div data-instagram-tutorial-target="VIDEO_STUDIO_STYLE">
                        <span className="setting-section-title">영상 스타일 <span className="setting-section-hint">선택하지 않아도 생성할 수 있어요.</span></span>
                        {this.renderPills(VIDEO_STYLE_OPTIONS, this.state.videoStyleCd, (value) => this.setOption('videoStyleCd', value), undefined, true)}
                    </div>
                    <div data-instagram-tutorial-target="VIDEO_STUDIO_CAMERA_ANGLE">
                        <span className="setting-section-title">촬영 구도 <span className="setting-section-hint">선택하지 않아도 생성할 수 있어요.</span></span>
                        {this.renderPills(CAMERA_ANGLE_OPTIONS, this.state.cameraAngleCd, (value) => this.setOption('cameraAngleCd', value), undefined, true)}
                    </div>
                    <div data-instagram-tutorial-target="VIDEO_STUDIO_CAMERA_MOVEMENT">
                        <span className="setting-section-title">카메라 움직임 <span className="setting-section-hint">선택하지 않아도 생성할 수 있어요.</span></span>
                        {this.renderPills(CAMERA_MOVEMENT_OPTIONS, this.state.cameraMovementCd, (value) => this.setOption('cameraMovementCd', value), undefined, true)}
                    </div>
                    {highResolution ? (
                        <div className="generation-note">1080p와 4K는 8초 영상으로 생성되므로 영상 길이가 자동으로 8초로 설정됩니다.</div>
                    ) : null}
                    {hasLastFrameImage && !highResolution ? (
                        <div className="generation-note">마지막 이미지를 사용하면 Veo의 마지막 프레임 생성 방식에 맞춰 영상 길이가 8초로 고정됩니다.</div>
                    ) : null}
                    {hasReferenceImages ? (
                        <div className="generation-note">참조 이미지를 사용하면 영상 길이는 8초로 고정되며 시작 이미지·마지막 이미지와 함께 사용할 수 없습니다.</div>
                    ) : null}
                        <div className="generation-note">
                            사용자가 직접 입력한 내용이 가장 우선적으로 반영되며, 영상 스타일·촬영 구도·카메라 움직임은 프롬프트에 보조 지시로 추가됩니다.
                        </div>
                    </div>
                </div>

                <div className="generate-footer">
                    <button
                        type="button"
                        className="btn btn-primary"
                        data-instagram-tutorial-target="VIDEO_STUDIO_GENERATE"
                        onClick={this.generateVideo}
                        disabled={this.state.isGenerating}
                    >
                        {this.state.isGenerating ? '영상 생성 중...' : (this.state.results.length ? '새 영상 생성' : '영상 생성')}
                    </button>
                </div>
            </div>
        );
    };

    handleVideoLoadStart = (event) => {
        const video = event && event.currentTarget;
        const videoUrl = video
            ? String((video.dataset && video.dataset.videoUrl) || video.currentSrc || video.src || '')
            : '';

        // 동일 URL의 video 엘리먼트가 새로 로드되는 경우에도 준비 상태를 다시 확인합니다.
        if (videoUrl && String(this.state.videoReadyUrl || '') === videoUrl) {
            this.setState({ videoReadyUrl: '' });
        }
    };

    handleVideoReady = (event) => {
        const video = event && event.currentTarget;
        if (!video) return;

        // preload="metadata"에서는 canplaythrough가 발생하지 않을 수 있습니다.
        // 메타데이터만 준비되어도 컨트롤을 노출해 사용자가 즉시 재생을 시작할 수 있게 합니다.
        if (video.readyState < 1) return;

        // DOM의 currentSrc는 브라우저가 절대 URL로 정규화할 수 있으므로,
        // 렌더링에 사용한 원본 URL을 data 속성으로 보관해 같은 값으로 비교합니다.
        const videoUrl = String((video.dataset && video.dataset.videoUrl) || video.currentSrc || video.src || '');
        if (!videoUrl) return;

        if (String(this.state.videoReadyUrl || '') !== videoUrl) {
            this.setState({ videoReadyUrl: videoUrl });
        }
    };

    renderResultPane = () => {
        const result = this.getPreviewResult();
        const results = this.state.results || [];
        const status = result ? String(result.run_status || '').toUpperCase() : '';
        const ratio = normalizeAspectRatio(result?.aspect_ratio || this.state.aspectRatio || '9:16');
        const isDone = status === 'DONE' && result?.generated_video_url;
        const isProcessing = status === 'PROCESSING' || status === 'READY';
        const isFail = status === 'FAIL' || status === 'FAILED' || status === 'UPLOAD_FAILED';
        const videoUrl = isDone ? String(result.generated_video_url || '') : '';
        const posterUrl = isDone
            ? String(result.generated_thumbnail_url || result.thumbnail_url || result.preview_image_url || '')
            : '';

        return (
            <div className="result-pane" data-instagram-tutorial-target="VIDEO_STUDIO_RESULT">
                <div className="result-header">
                    <div className="result-title">생성 결과</div>
                    <div className="result-count">{results.length ? `총 ${results.length}개` : '아직 생성된 영상이 없습니다.'}</div>
                </div>
                <div className="result-body-scroll">
                    <div className="video-canvas-wrap">
                        <div className={`video-canvas ${ratio === '16:9' ? 'ratio-horizontal' : 'ratio-vertical'}`}>
                        {!result ? (
                            <div className="video-default">
                                <div className="video-default-title">생성된 영상이 여기에 표시됩니다.</div>
                                <div>왼쪽에서 내용을 입력하고 영상 생성을 시작해 주세요.</div>
                            </div>
                        ) : isDone ? (
                            <>
                                {this.state.tutorialDemoMode ? (
                                    <>
                                        <video
                                            key={result.generated_video_url}
                                            className="video-preview"
                                            src={result.generated_video_url}
                                            controls
                                            playsInline
                                            preload="metadata"
                                        />
                                        <div className="tutorial-demo-badge">튜토리얼 예시</div>
                                    </>
                                ) : (
                                    <video
                                        className="video-preview"
                                        src={videoUrl}
                                        poster={posterUrl || undefined}
                                        controls
                                        muted
                                        playsInline
                                        onError={(event) => {
                                            const video = event && event.currentTarget;
                                            console.error('[AI Video Studio] preview video error', {
                                                src: video ? (video.currentSrc || video.src) : videoUrl,
                                                error: video ? video.error : null,
                                                networkState: video ? video.networkState : null,
                                                readyState: video ? video.readyState : null,
                                            });
                                        }}
                                    />
                                )}
                                <div className="preview-status-badge">결과 {result.result_no || this.state.previewIndex + 1}</div>
                                <button type="button" className="download-icon-btn" data-instagram-tutorial-target="VIDEO_STUDIO_RESULT_DOWNLOAD" onClick={this.downloadCurrentVideo} title="영상 다운로드">↓</button>
                            </>
                        ) : isProcessing ? (
                            <div className="video-loading">
                                <div className="spinner" />
                                <strong>영상을 생성하고 있습니다.</strong>
                                <span style={{ fontSize: 12 }}>완료되면 이 영역에 자동으로 표시됩니다.</span>
                            </div>
                        ) : isFail ? (
                            <div className="video-error">
                                <strong>영상을 생성하지 못했습니다.</strong><br />
                                {result.error_message || '다른 내용으로 다시 시도해 주세요.'}
                            </div>
                        ) : (
                            <div className="video-default">영상 상태를 확인하고 있습니다.</div>
                        )}

                        {results.length > 1 ? (
                            <div className="history-nav-overlay" data-instagram-tutorial-target="VIDEO_STUDIO_RESULT_HISTORY">
                                <button type="button" className={`history-nav-btn ${this.state.previewIndex <= 0 ? 'disabled' : ''}`} onClick={() => this.state.previewIndex > 0 && this.movePreview(-1)}>‹</button>
                                <div className="history-nav-index">{this.state.previewIndex + 1} / {results.length}</div>
                                <button type="button" className={`history-nav-btn ${this.state.previewIndex >= results.length - 1 ? 'disabled' : ''}`} onClick={() => this.state.previewIndex < results.length - 1 && this.movePreview(1)}>›</button>
                            </div>
                        ) : null}
                        </div>
                    </div>
                    {result ? (
                        <div className="result-meta">
                            {ratio} · {result.duration_seconds || 8}초 · {result.resolution || '720p'}
                        </div>
                    ) : null}
                </div>
            </div>
        );
    };

    renderFooter = () => {
        const listMode = this.isOpenedFromAiStudioList();
        const responseType = this.getAiResponseType();

        let title = '선택한 영상을 적용해볼까요?';
        let desc = '지금 보고 있는 영상을 바로 적용할 수 있어요.';
        if (responseType === 'PROMPT') {
            title = '이 영상 생성 설정을 자동화에 적용할까요?';
            desc = '현재 입력한 프롬프트와 영상 설정이 자동화 규칙으로 저장됩니다.';
        }
        if (listMode) {
            title = '어디에 적용할까요?';
            desc = '현재 영상은 게시물 등록에, 현재 영상 생성 설정은 자동화 규칙에 적용할 수 있어요.';
        }

        return (
            <div className="workspace-footer" data-instagram-tutorial-target="VIDEO_STUDIO_FOOTER">
                <div className="footer-guide">
                    <div className="footer-guide-icon">✓</div>
                    <div style={{ minWidth: 0 }}>
                        <div className="footer-guide-title">{title}</div>
                        <div className="footer-guide-desc">{desc}</div>
                    </div>
                </div>
                <div className="footer-actions">
                    {listMode ? (
                        <>
                            {/* <button type="button" className="btn btn-outline" onClick={() => this.applyToMain('PROMPT')}>자동화 규칙으로 적용</button> */}
                            <button type="button" className="btn btn-primary" data-instagram-tutorial-target="VIDEO_STUDIO_FOOTER_REGISTER_VIDEO" onClick={() => this.applyToMain('VIDEO')}>현재 선택된 영상으로 콘텐츠 등록</button>
                        </>
                    ) : responseType === 'PROMPT' ? (
                        <button type="button" className="btn btn-primary" data-instagram-tutorial-target="VIDEO_STUDIO_FOOTER_APPLY_RULE" onClick={() => this.applyToMain('PROMPT')}>자동화 규칙으로 적용</button>
                    ) : (
                        <button type="button" className="btn btn-primary" data-instagram-tutorial-target="VIDEO_STUDIO_FOOTER_APPLY_VIDEO" onClick={() => this.applyToMain('VIDEO')}>현재 선택된 영상을 적용</button>
                    )}
                </div>
            </div>
        );
    };

    renderStudioContent = () => {
        const groupedHistory = this.groupHistoryByDate(this.state.historyItems);
        return (
            <div className={`modal-box ${this.isPopupMode() ? '' : 'full-page-mode'}`}>
                <div className="studio-header">
                    <div className="studio-logo">
                        <img src="https://robomd.io/images/home/logo_favicon.svg" alt="logo" style={{ width: 24, height: 24 }} />
                        로보MD AI 동영상 스튜디오
                        <button type="button" className="mobile-history-btn" onClick={this.toggleMobileHistory}>히스토리</button>
                    </div>
                    {this.isPopupMode() ? <div className="studio-close" onClick={this.closeModal}>✕</div> : null}
                </div>

                <div className="studio-body" style={{ position: 'relative' }}>
                    {this.state.isMobileHistoryOpen ? <div className="mobile-history-backdrop" onClick={this.closeMobileHistory} /> : null}
                    <div className={`studio-sidebar ${this.state.isMobileHistoryOpen ? 'mobile-open' : ''}`}>
                        <div className="sidebar-header">
                            <button type="button" className="new-chat-btn" onClick={this.startNewWorkflow}>
                                <span>새로운 영상 만들기</span><span style={{ fontSize: 20 }}>＋</span>
                            </button>
                        </div>
                        <div className="history-filter-wrap">
                            <button type="button" className={`history-filter-btn ${this.state.auto_yn === 'N' ? 'active' : ''}`} onClick={() => this.setState({ auto_yn: 'N' }, () => this.loadHistoryList('N'))}>내 히스토리</button>
                            <button type="button" className={`history-filter-btn ${this.state.auto_yn === 'Y' ? 'active' : ''}`} onClick={() => this.setState({ auto_yn: 'Y' }, () => this.loadHistoryList('Y'))}>자동 히스토리</button>
                        </div>
                        <div className="history-list">
                            {Object.keys(groupedHistory).map((groupKey) => (
                                <React.Fragment key={groupKey}>
                                    <div className="history-group-title">{groupKey}</div>
                                    {groupedHistory[groupKey].map((item) => {
                                        const seq = item.ai_video_studio_history_seq;
                                        const active = String(this.state.selectedHistorySeq || '') === String(seq || '');
                                        const menuOpen = String(this.state.historyMenuSeq || '') === String(seq || '');
                                        return (
                                            <div key={seq} className={`history-item ${active ? 'active' : ''}`} onClick={() => this.handleHistoryItemClick(item)} title={item.history_title || 'AI 동영상 히스토리'}>
                                                <div className="history-item-text">{item.history_title || '새 영상 생성'}</div>
                                                <span className="history-menu-wrap">
                                                    <button type="button" className="history-menu-trigger" onClick={(event) => this.toggleHistoryMenu(event, item)}>⋯</button>
                                                    {menuOpen ? (
                                                        <span className="history-menu" onClick={(event) => event.stopPropagation()}>
                                                            <button type="button" onClick={(event) => this.handleHistoryRename(event, item)}>이름 변경</button>
                                                            <button type="button" onClick={(event) => this.handleHistoryDuplicate(event, item)}>새 작업으로 복제</button>
                                                            <button type="button" className="delete" onClick={(event) => this.handleHistoryDelete(event, item)}>삭제</button>
                                                        </span>
                                                    ) : null}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </div>
                        {this.state.aiUsage["is_api_limit_apply_yn"] === 'Y' && (
                            <div className="usage-summary-box">
                                <div className="usage-summary-title">
                                    AI 영상 호출량
                                </div>
                                <div className="usage-summary-body">
                                    {this.state.aiUsageLoading ? (
                                        <span className="usage-loading-text">불러오는 중...</span>
                                    ) : (
                                        <>
                                            <span className="usage-main-text">
                                                {Number(this.state.aiUsage.total_used_veo_call_count || 0).toLocaleString()}
                                                <span className="usage-separator"> / </span>
                                                {Number(this.state.aiUsage.total_available_veo_call_count || 0).toLocaleString()}
                                            </span>
                                            <span className="usage-sub-text">
                                                남은 횟수 {Number(this.state.aiUsage.max_available_veo_call_count || 0).toLocaleString()}회
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="studio-main-wrap">
                        <div className="workspace-scroll">
                            <div className="workspace-card">
                                {this.renderConfigPane()}
                                {this.renderResultPane()}
                            </div>
                        </div>
                        {this.isPopupMode() ? this.renderFooter() : null}
                    </div>
                </div>
            </div>
        );
    };

    render() {
        return (
            <>
                <div
                    className="ai-video-studio-workflow"
                    data-instagram-tutorial-popup={this.isPopupMode() ? "POPUP_AI_VIDEO_STUDIO" : undefined}
                >
                    <style>{style_css}</style>
                    {this.isPopupMode() ? (
                        <div className="modal-overlay">{this.renderStudioContent()}</div>
                    ) : this.renderStudioContent()}
                    {this.renderImageSourceModal()}
                </div>

                {this.state.showAiImageStudioPopup ? (
                    <AiImageStudioWorkflowClass
                        view_mode="popup"
                        ai_response_type="IMAGE"
                        service_type={this.getServiceType()}
                        initial_prompt=""
                        initial_image_url=""
                        onClose={this.closeAiImageStudioPopup}
                        onApplyRule={this.handleAiImageStudioApply}
                    />
                ) : null}

                {this.state.historyRenameDialogOpen && this.state.historyRenameTarget ? (
                    <InputDialogV3
                        title="AI 동영상 스튜디오 히스토리 이름 변경"
                        content="변경할 히스토리 이름을 입력해주세요."
                        input_list={[{
                            title: '히스토리 이름',
                            value: this.state.historyRenameTarget.history_title || '새 영상 생성',
                            placeholder: '히스토리 이름을 입력해주세요.',
                        }]}
                        button_text="변경"
                        is_show_close={true}
                        onPopupClose={this.closeHistoryRenameDialog}
                        onButtonClick={this.confirmHistoryRename}
                    />
                ) : null}

                {this.state.historyDeleteDialogOpen && this.state.historyDeleteTarget ? (
                    <TitleContentDialogV2
                        title="AI 동영상 스튜디오 히스토리를 삭제하시겠습니까?"
                        content="삭제한 히스토리는 복구할 수 없습니다."
                        popup_close={this.closeHistoryDeleteDialog}
                        button_list={[
                            { title: this.state.historyActionProcessing ? '삭제 중.' : '삭제', event_method: this.deleteHistory },
                            { title: '취소', event_method: this.closeHistoryDeleteDialog },
                        ]}
                    />
                ) : null}

                {this.state.warning_dialog_show_state ? (
                    <WarningDialog
                        content={this.state.warning_dialog_content}
                        button_list={this.state.warning_dialog_button_list}
                        width_auto={this.state.warning_dialog_width_auto}
                    />
                ) : null}

                {this.state.save_dialog_show_state ? (
                    <SaveDialogV2
                        id="save_dialog"
                        content={this.state.save_dialog_content}
                        button_list={this.state.save_dialog_button_list}
                    />
                ) : null}
            </>
        );
    }
}

AiVideoStudioWorkflowClass.defaultProps = {
    view_mode: 'popup',
    ai_response_type: 'VIDEO',
    opened_from: '',
    aspect_ratio: '9:16',
    initial_prompt: '',
    initial_sample_prompt: '',
    initial_sample_mode: false,
    initial_sample_title: '',
    initial_sample_scene_description: '',
    initial_sample_image_guide: '',
    initial_sample_user_reference_description: '',
    initial_sample_user_reference_image: null,
    initial_image_url: '',
    initial_source_image_url: '',
    initial_reference_images: [],
    initial_last_frame_image_url: '',
    initial_history_seq: '',
    initial_result_seq: '',
    initial_result_no: '',
    initial_result_video_url: '',
    initial_video_style_cd: '',
    initial_camera_angle_cd: '',
    initial_camera_movement_cd: '',
    initial_aspect_ratio: '',
    initial_duration_seconds: 8,
    initial_resolution: '720p',
    service_type: '01014',
};